import { AppleAppStoreState, AppPlatform, ReleaseType, StabilityIssue } from './../types';
import { RecursiveStringifyEnums } from './buildDistro';
import { isAndroidPlatform } from '../helpers';
import { iosStepAutomations, androidStepAutomations } from '@/testData/stepAutomations';
import { featuresStep, getCompactDataFromFeaturesStep } from '@/testData/featureItems';
import { dayjs } from '@/extendedDayjs';

export const topStabilityIssues: (platform: AppPlatform) => RecursiveStringifyEnums<StabilityIssue[]> = (platform) => {
    return isAndroidPlatform(platform) ?
            [
                {
                    issueID: '6240d9ca9fcede000841ac61',
                    crashShortDescription: 'NullPointerException',
                    crashLongDescription:
                        'A NullPointerException is being thrown when the app tries to reference a null object in the LoginActivity. It seems the application is trying to access an object that has not been initialized. The issue is specifically found at line 55 in LoginActivity.java.',
                    blameFile: null,
                    blameLine: null,
                    occurrencesCountInRelease: 2,
                    totalOccurrencesCount: 2,
                    url: 'https://app.sentry.com/demo/123456',
                    isNewInRelease: false,
                    isNonFatal: false,
                    stackTrace: [],
                    associatedCommits: [],
                },
                {
                    issueID: '623a7f2d08867c000975c970',
                    crashShortDescription: 'SQLiteConstraintException',
                    crashLongDescription:
                        "There's an SQLiteConstraintException occurring in the DatabaseHandler class. This typically means there's a violation of a foreign key constraint. Review the database structure and check the method at line 120 in 'DatabaseHandler.java",
                    blameFile: null,
                    blameLine: null,
                    occurrencesCountInRelease: 1,
                    totalOccurrencesCount: 1,
                    url: 'https://app.sentry.com/demo/123456',
                    isNewInRelease: false,
                    isNonFatal: false,
                    stackTrace: [],
                    associatedCommits: [],
                },
                {
                    issueID: '6240b9495b30f50009c8a33a',
                    crashShortDescription: 'NumberFormatException',
                    crashLongDescription:
                        "A NumberFormatException is being thrown in the InputDataFragment. It seems the app is trying to parse a string into a numeric format, but the string doesn't have an appropriate format. The error is found at line 178 in 'InputDataFragment.java'",
                    blameFile: null,
                    blameLine: null,
                    occurrencesCountInRelease: 1,
                    totalOccurrencesCount: 1,
                    url: 'https://app.sentry.com/demo/123456',
                    isNewInRelease: false,
                    isNonFatal: false,
                    stackTrace: [],
                    associatedCommits: [],
                },
                {
                    issueID: '6240be6f9fbe8c0008d27ed8',
                    crashShortDescription: 'IllegalStateException',
                    crashLongDescription:
                        "An IllegalStateException is being raised in MainActivity. The app seems to be making a call that's not currently legal in the context of the current state. The problematic invocation is at line 230 in 'MainActivity.java'",
                    blameFile: null,
                    blameLine: null,
                    occurrencesCountInRelease: 1,
                    totalOccurrencesCount: 1,
                    url: 'https://app.sentry.com/demo/123456',
                    isNewInRelease: false,
                    isNonFatal: false,
                    stackTrace: [],
                    associatedCommits: [],
                },
                {
                    issueID: '6241f7c418309100082209d9',
                    crashShortDescription: 'OutOfMemoryError',
                    crashLongDescription:
                        "The app has run out of memory. This error is thrown when there is insufficient memory to allocate an object in the heap or the heap has exhausted its memory. You may need to optimize your app's memory usage or request a larger heap size for your app",
                    blameFile: null,
                    blameLine: null,
                    occurrencesCountInRelease: 1,
                    totalOccurrencesCount: 1,
                    url: 'https://app.sentry.com/demo/123456',
                    isNewInRelease: false,
                    isNonFatal: false,
                    stackTrace: [],
                    associatedCommits: [],
                },
            ]
        :   [
                {
                    issueID: '6240d9ca9fcede000841ac61',
                    crashShortDescription: 'Out Of Memory',
                    crashLongDescription:
                        'The app was likely terminated by the operating system while in the foreground',
                    blameFile: null,
                    blameLine: null,
                    occurrencesCountInRelease: 2,
                    totalOccurrencesCount: 2,
                    url: 'https://app.sentry.com/demo/123456',
                    isNewInRelease: false,
                    isNonFatal: false,
                    stackTrace: [],
                    associatedCommits: [],
                },
                {
                    issueID: '623a7f2d08867c000975c970',
                    crashShortDescription: 'NSInternalInconsistencyException',
                    crashLongDescription:
                        'Invalid update: invalid number of rows in section 0. The number of rows contained in an existing section after the update (9) must be equal to the number of rows contained in that section before the update (7), plus or minus the number of rows inserted or deleted from that section (1 inserted, 1 deleted) and plus or minus the number of rows moved into or out of that section (0 moved in, 0 moved out).',
                    blameFile: null,
                    blameLine: null,
                    occurrencesCountInRelease: 1,
                    totalOccurrencesCount: 1,
                    url: 'https://app.sentry.com/demo/123456',
                    isNewInRelease: false,
                    isNonFatal: false,
                    stackTrace: [],
                    associatedCommits: [],
                },
                {
                    issueID: '6240b9495b30f50009c8a33a',
                    crashShortDescription: 'SIGTRAP',
                    crashLongDescription: '',
                    blameFile: null,
                    blameLine: null,
                    occurrencesCountInRelease: 1,
                    totalOccurrencesCount: 1,
                    url: 'https://app.sentry.com/demo/123456',
                    isNewInRelease: false,
                    isNonFatal: false,
                    stackTrace: [],
                    associatedCommits: [],
                },
                {
                    issueID: '6240be6f9fbe8c0008d27ed8',
                    crashShortDescription: 'App Hang',
                    crashLongDescription: 'The app was terminated while unresponsive',
                    blameFile: null,
                    blameLine: null,
                    occurrencesCountInRelease: 1,
                    totalOccurrencesCount: 1,
                    url: 'https://app.sentry.com/demo/123456',
                    isNewInRelease: false,
                    isNonFatal: false,
                    stackTrace: [],
                    associatedCommits: [],
                },
                {
                    issueID: '6241f7c418309100082209d9',
                    crashShortDescription: 'NSInvalidArgumentException',
                    crashLongDescription: '*** -[__NSArrayM insertObject:atIndex:]: object cannot be nil',
                    blameFile: null,
                    blameLine: null,
                    occurrencesCountInRelease: 1,
                    totalOccurrencesCount: 1,
                    url: 'https://app.sentry.com/demo/123456',
                    isNewInRelease: false,
                    isNonFatal: false,
                    stackTrace: [],
                    associatedCommits: [],
                },
            ];
};

export const topNewStabilityIssues: (platform: AppPlatform) => RecursiveStringifyEnums<StabilityIssue[]> = (
    platform,
) => {
    return isAndroidPlatform(platform) ?
            [
                {
                    issueID: '6240d9ca9fcede000841ac61',
                    crashShortDescription: 'ANR (Application Not Responding)',
                    crashLongDescription:
                        'The application has triggered an ANR, or Application Not Responding warning. This usually happens when the main UI thread has been blocked for too long, typically due to heavy computations or operations being performed on this thread. Offload intensive tasks to a background thread.',
                    blameFile: null,
                    blameLine: null,
                    occurrencesCountInRelease: 2,
                    totalOccurrencesCount: 2,
                    url: 'https://app.sentry.com/demo/123456',
                    isNewInRelease: false,
                    isNonFatal: false,
                    stackTrace: [],
                    associatedCommits: [],
                },
                {
                    issueID: '623a7f2d08867c000975c970',
                    crashShortDescription: 'ArrayIndexOutOfBoundsException',
                    crashLongDescription:
                        "An ArrayIndexOutOfBoundsException is being thrown in the ListViewAdapter. It appears the app is trying to access an array element using an index that is out of the boundaries of the array. Check the array indexing at line 80 in 'ListViewAdapter.java'.",
                    blameFile: null,
                    blameLine: null,
                    occurrencesCountInRelease: 1,
                    totalOccurrencesCount: 1,
                    url: 'https://app.sentry.com/demo/123456',
                    isNewInRelease: false,
                    isNonFatal: false,
                    stackTrace: [],
                    associatedCommits: [],
                },
                {
                    issueID: '623a7f2d08867c000975c970',
                    crashShortDescription: 'SQLiteConstraintException',
                    crashLongDescription:
                        "There's an SQLiteConstraintException occurring in the DatabaseHandler class. This typically means there's a violation of a foreign key constraint. Review the database structure and check the method at line 120 in 'DatabaseHandler.java",
                    blameFile: null,
                    blameLine: null,
                    occurrencesCountInRelease: 1,
                    totalOccurrencesCount: 1,
                    url: 'https://app.sentry.com/demo/123456',
                    isNewInRelease: false,
                    isNonFatal: false,
                    stackTrace: [],
                    associatedCommits: [],
                },
            ]
        :   [
                {
                    issueID: '6240d9ca9fcede000841ac61',
                    crashShortDescription: 'Out Of Memory',
                    crashLongDescription:
                        'The app was likely terminated by the operating system while in the foreground',
                    blameFile: null,
                    blameLine: null,
                    occurrencesCountInRelease: 2,
                    totalOccurrencesCount: 2,
                    url: 'https://app.sentry.com/demo/123456',
                    isNewInRelease: false,
                    isNonFatal: false,
                    stackTrace: [],
                    associatedCommits: [],
                },
                {
                    issueID: '623a7f2d08867c000975c970',
                    crashShortDescription: 'NSInternalInconsistencyException',
                    crashLongDescription:
                        'Invalid update: invalid number of rows in section 0. The number of rows contained in an existing section after the update (9) must be equal to the number of rows contained in that section before the update (7), plus or minus the number of rows inserted or deleted from that section (1 inserted, 1 deleted) and plus or minus the number of rows moved into or out of that section (0 moved in, 0 moved out).',
                    blameFile: null,
                    blameLine: null,
                    occurrencesCountInRelease: 1,
                    totalOccurrencesCount: 1,
                    url: 'https://app.sentry.com/demo/123456',
                    isNewInRelease: false,
                    isNonFatal: false,
                    stackTrace: [],
                    associatedCommits: [],
                },
                {
                    issueID: '6240b9495b30f50009c8a33a',
                    crashShortDescription: 'SIGTRAP',
                    crashLongDescription: '',
                    blameFile: null,
                    blameLine: null,
                    occurrencesCountInRelease: 1,
                    totalOccurrencesCount: 1,
                    url: 'https://app.sentry.com/demo/123456',
                    isNewInRelease: false,
                    isNonFatal: false,
                    stackTrace: [],
                    associatedCommits: [],
                },
                {
                    issueID: '6240be6f9fbe8c0008d27ed8',
                    crashShortDescription: 'App Hang',
                    crashLongDescription: 'The app was terminated while unresponsive',
                    blameFile: null,
                    blameLine: null,
                    occurrencesCountInRelease: 1,
                    totalOccurrencesCount: 1,
                    url: 'https://app.sentry.com/demo/123456',
                    isNewInRelease: false,
                    isNonFatal: false,
                    stackTrace: [],
                    associatedCommits: [],
                },
                {
                    issueID: '6241f7c418309100082209d9',
                    crashShortDescription: 'NSInvalidArgumentException',
                    crashLongDescription: '*** -[__NSArrayM insertObject:atIndex:]: object cannot be nil',
                    blameFile: null,
                    blameLine: null,
                    occurrencesCountInRelease: 1,
                    totalOccurrencesCount: 1,
                    url: 'https://app.sentry.com/demo/123456',
                    isNewInRelease: false,
                    isNonFatal: false,
                    stackTrace: [],
                    associatedCommits: [],
                },
            ];
};

export const appHomeMock = (platform: AppPlatform) => ({
    liveRelease: {
        id: 'demo:1.0.0',
        isKickedOff: true,
        type: ReleaseType.Minor,
        releaseId: 'demo:1.0.0',
        version: '1.0.0',
        status: 'active',
        releaseDate: '2022-03-03T15:59:22.488042904Z',
        displayableVersion: '1.0.0',
        releaseName: null,
        lifecycleAutomations: [],
        rolloutMonitoring: {
            releaseAdoptions: {},
            monitoringStatistics: [],
            monitoringAutomations: [],
            checklistItems: [],
            automationRuns: [],
            featureFlagTimeSeries: [],
            appStoreSelectedBuilds: [],
            appStoreReviewData: null,
            rolloutStartedAt: '',
        },
        completedAt: dayjs().subtract(1, 'day').toISOString(),
        featuresStep: getCompactDataFromFeaturesStep(featuresStep(platform)),
        steps:
            platform === AppPlatform.Android ?
                [
                    {
                        id: '1.0.0#kickoff',
                        type: 'kickoff',
                        description: 'Kickoff',
                        status: 'ready',
                        statusReasonString: 'Kicked off!',
                        blockingConfig: 'Blocking',
                        data: {
                            appUsesStaticReleaseBranch: false,
                            releaseBranchCodeVersionData: {
                                branchName: 'main',
                                bumpVersionPullRequest: null,
                                bumpVersionStatus: 'detected',
                                canPerformBumpVersion: false,
                                currentCodeVersionExternalString: '1.0.0',
                                targetCodeVersionExternalString: '1.0.0',
                                targetCodeVersionInternalString: '1.0.0',
                            },
                            workingBranchCodeVersionData: {
                                branchName: 'master',
                                bumpVersionPullRequest: {
                                    id: 'PR_123',
                                    number: 96,
                                    pullRequestType: 'BumpVersion',
                                    status: 'merged',
                                    title: 'Bumping version',
                                    url: 'https://github.com/runway-org/fake-app/pull/96',
                                },
                                bumpVersionStatus: 'merged',
                                canPerformBumpVersion: false,
                                currentCodeVersionExternalString: '1.0.0',
                                targetCodeVersionExternalString: '1.0.0',
                                targetCodeVersionInternalString: '1.0.0',
                            },
                            canPerformCodePromotion: false,
                            codePromotionPullRequest: null,
                            codePromotionStatus: '',
                            doesReleaseBranchExist: true,
                            hasPerformedAnyCodePromotion: false,
                            hotfixBranchingBase: null,
                            previousReleaseTag: 'v1.24.0',
                            previousReleaseVersion: '1.24.0',
                            previousTaggedReleaseTag: 'v1.24.0',
                            previousTaggedReleaseTagVersionsBehind: 0,
                            releaseBranch: 'release-1.25.0',
                            releaseBranchDiffUrl: 'https://github.com',
                            releaseBranchUrl: 'https://github.com',
                            resolvedFeatureAffiliations: [
                                {
                                    affiliationType: 'jira-fix-version',
                                    integrationId: 'jira',
                                    resolvedString: '3.97.0',
                                },
                            ],
                            workingBranchDiffUrl: null,
                        },
                        groupIndex: 0,
                        orderIndex: 0,
                        checklistItems: [
                            {
                                id: 'abc',
                                title: 'Pre-kickoff reminders',
                                description: 'Description   \u003cb\u003eyo\u003c/b\u003e !!',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'kickoff',
                                approverGroups: ['pilot'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: {
                                    status: 'approved',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'def',
                                title: 'Send out reminder for upcoming release deadlines',
                                description: 'Some detail',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'kickoff',
                                approverGroups: ['pilot'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: {
                                    status: 'approved',
                                    createdAt: '2022-03-03T18:03:32.236911544Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                comment: {
                                    comment: 'Reminder sent via email',
                                    createdAt: '2022-03-03T18:00:32.236911544Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'jkl',
                                title: 'Verify versioning with product team',
                                description: null,
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'kickoff',
                                approverGroups: [],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: {
                                    status: 'approved',
                                    createdAt: '2022-03-03T18:03:33.133516479Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: androidStepAutomations['kickoff'],
                    },
                    {
                        id: '1.0.0#featureReadiness',
                        type: 'featureReadiness',
                        description: 'Feature readiness',
                        status: 'pending',
                        statusReasonString: 'Some work for this release is not yet complete.',
                        blockingConfig: 'Blocking',
                        data: { items: null, percentComplete: 0, workingBranchPercentComplete: null },
                        groupIndex: 0,
                        orderIndex: 1,
                        checklistItems: [
                            {
                                id: 'abc',
                                title: 'Verify production feature flags are correct for this release',
                                description: null,
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'featureReadiness',
                                approverGroups: ['engineer'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: {
                                    status: 'approved',
                                    createdAt: '2022-03-15T22:54:38.462070296Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'def',
                                title: 'Update CMS version flags for this release',
                                description: '',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'featureReadiness',
                                approverGroups: [],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: {
                                    status: 'approved',
                                    createdAt: '2022-03-15T22:54:39.472656772Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: androidStepAutomations['featureReadiness'],
                    },
                    {
                        id: '1.0.0#releaseCandidate',
                        type: 'releaseCandidate',
                        description: 'CI',
                        status: 'ready',
                        statusReasonString: 'CI build is ready!',
                        blockingConfig: 'Blocking',
                        data: {
                            builds: [
                                {
                                    associatedAppStoreBuild: {
                                        betaBuildDetails: { status: 'completed', userFraction: 0 },
                                        buildNumber: '27500',
                                        id: '27500',
                                        integrationId: 'google-beta',
                                        isProcessed: true,
                                        status: 'completed',
                                        type: 'android-aab',
                                        uploadedAt: '2022-03-15T18:11:14.055381Z',
                                        userFraction: 0,
                                    },
                                    ciBuild: {
                                        artifacts: [],
                                        branch: 'release-1.25.0',
                                        buildFinishedAt: '2022-03-15T18:09:36Z',
                                        buildHash: '5dGgafjgm3ECgAvSmUmyok8bFZY=',
                                        buildIdentifier: '275',
                                        buildStartedAt: '2022-03-15T18:06:21Z',
                                        buildStatus: 'success',
                                        buildUrl: 'https://app.bitrise.io/build',
                                        commit: '8377f75e11ef0544ac5d7b138f8ec1af913203be',
                                        commitAuthor: 'runwayhelperdev[bot]',
                                        commitMessage:
                                            'Bumping version (#41)\n\nCo-authored-by: runwayhelperdev[bot] \u003c68658124+runwayhelperdev[bot]@users.noreply.github.com\u003e',
                                        integrationId: 'bitrise',
                                        providerBuildStatusString: 'Success',
                                        workflowRunId: '1234',
                                    },
                                    isSelectedBuild: true,
                                    selectedReason: 'autoRunwaySelection',
                                },
                                {
                                    associatedAppStoreBuild: {
                                        betaBuildDetails: { status: null, userFraction: 0 },
                                        buildNumber: '27100',
                                        id: '27100',
                                        integrationId: 'google-beta',
                                        isProcessed: true,
                                        status: null,
                                        type: 'android-aab',
                                        uploadedAt: '2022-03-15T15:53:26.217791Z',
                                        userFraction: 0,
                                    },
                                    ciBuild: {
                                        artifacts: [],
                                        branch: 'release-1.25.0',
                                        buildFinishedAt: '2022-03-15T15:53:31Z',
                                        buildHash: 'abc',
                                        buildIdentifier: '271',
                                        buildStartedAt: '2022-03-15T15:50:06Z',
                                        buildStatus: 'success',
                                        buildUrl: 'https://app.bitrise.io/build',
                                        commit: '8377f75e11ef0544ac5d7b138f8ec1af913203be',
                                        commitAuthor: 'runwayhelperdev[bot]',
                                        commitMessage:
                                            'Bumping version (#41)\n\nCo-authored-by: runwayhelperdev[bot] \u003c68658124+runwayhelperdev[bot]@users.noreply.github.com\u003e',
                                        integrationId: 'bitrise',
                                        providerBuildStatusString: 'Success',
                                        workflowRunId: '1234',
                                    },
                                    isSelectedBuild: false,
                                    selectedReason: 'autoRunwaySelection',
                                },
                                {
                                    associatedAppStoreBuild: {
                                        betaBuildDetails: { status: null, userFraction: 0 },
                                        buildNumber: '26600',
                                        id: '26600',
                                        integrationId: 'google-beta',
                                        isProcessed: true,
                                        status: null,
                                        type: 'android-aab',
                                        uploadedAt: '2022-02-22T15:10:44.431772-06:00',
                                        userFraction: 0,
                                    },
                                    ciBuild: {
                                        artifacts: [],
                                        branch: 'release-1.25.0',
                                        buildFinishedAt: '2022-02-22T21:09:21Z',
                                        buildHash: 'xIF7XbFDz_ILtKZjP3TOnVythwo=',
                                        buildIdentifier: '266',
                                        buildStartedAt: '2022-02-22T21:05:24Z',
                                        buildStatus: 'success',
                                        buildUrl: 'https://app.bitrise.io/build/339716b3-ad88-417a-a16c-6b291ed196db',
                                        commit: '8377f75e11ef0544ac5d7b138f8ec1af913203be',
                                        commitAuthor: 'runwayhelperdev[bot]',
                                        commitMessage:
                                            'Bumping version to 1.25.0 (#41)\n\nCo-authored-by: runwayhelperdev[bot] \u003c68658124+runwayhelperdev[bot]@users.noreply.github.com\u003e',
                                        integrationId: 'bitrise',
                                        providerBuildStatusString: 'Success',
                                        workflowRunId: '339716b3-ad88-417a-a16c-6b291ed196db',
                                    },
                                    isSelectedBuild: false,
                                    selectedReason: 'autoRunwaySelection',
                                },
                                {
                                    associatedAppStoreBuild: {
                                        betaBuildDetails: { status: null, userFraction: 0 },
                                        buildNumber: '26300',
                                        id: '26300',
                                        integrationId: 'google-beta',
                                        isProcessed: true,
                                        status: null,
                                        type: 'android-aab',
                                        uploadedAt: '2022-02-14T18:29:27.228176572Z',
                                        userFraction: 0,
                                    },
                                    ciBuild: {
                                        artifacts: [],
                                        branch: 'release-1.25.0',
                                        buildFinishedAt: '2022-02-14T18:29:57Z',
                                        buildHash: 'EoC1sXC38yCv2FysjX3xoT8joUE=',
                                        buildIdentifier: '263',
                                        buildStartedAt: '2022-02-14T18:26:48Z',
                                        buildStatus: 'success',
                                        buildUrl: 'https://app.bitrise.io/build/c8bc9aa2-864f-46a0-99c8-c38fff620763',
                                        commit: '2a9abf9fb4eb2355dfd1600b622ea4f63ee4405d',
                                        commitAuthor: null,
                                        commitMessage:
                                            'Bumping version to 1.24.0 (#40)\n\nCo-authored-by: runwayhelperdev[bot] \u003c68658124+runwayhelperdev[bot]@users.noreply.github.com\u003e',
                                        integrationId: 'bitrise',
                                        providerBuildStatusString: 'Success',
                                        workflowRunId: 'c8bc9aa2-864f-46a0-99c8-c38fff620763',
                                    },
                                    isSelectedBuild: false,
                                    selectedReason: 'autoRunwaySelection',
                                },
                            ],
                            workflowData: {
                                branch: 'release-1.25.0',
                                headCommit: '8377f75e11ef0544ac5d7b138f8ec1af913203be',
                                headCommitAuthor: 'runwayhelperdev[bot]',
                                headCommitMessage:
                                    'Bumping version to 1.25.0 (#41)\n\nCo-authored-by: runwayhelperdev[bot] \u003c68658124+runwayhelperdev[bot]@users.noreply.github.com\u003e',
                                integrationType: 'bitrise',
                                workflowName: 'release',
                                workflowType: 'staging',
                                workflowUrl:
                                    'https://app.bitrise.io/app/d09c63f8c17bb806/workflow_editor#!/workflows?workflow_id=release',
                            },
                        },
                        groupIndex: 1,
                        orderIndex: 0,
                        checklistItems: [],
                        automationRuns: androidStepAutomations['releaseCandidate'],
                    },
                    {
                        id: '1.0.0#regressionTesting',
                        type: 'regressionTesting',
                        description: 'Regression testing',
                        status: 'pending',
                        statusReasonString: 'Regression testing in progress...',
                        blockingConfig: 'Blocking',
                        data: {
                            availableBuilds: [
                                {
                                    appStoreBuildIdentifier: '27500',
                                    appStoreIntegrationId: 'google',
                                    buildIdentifier: '275',
                                    hash: '5dGgafjgm3ECgAvSmUmyok8bFZY=',
                                    integrationId: 'bitrise',
                                },
                                {
                                    appStoreBuildIdentifier: '27100',
                                    appStoreIntegrationId: 'google',
                                    buildIdentifier: '271',
                                    hash: 'v_LYY2Q_tHP__uFMuoahvSvrjbs=',
                                    integrationId: 'bitrise',
                                },
                                {
                                    appStoreBuildIdentifier: '26600',
                                    appStoreIntegrationId: 'google',
                                    buildIdentifier: '266',
                                    hash: 'xIF7XbFDz_ILtKZjP3TOnVythwo=',
                                    integrationId: 'bitrise',
                                },
                                {
                                    appStoreBuildIdentifier: '26300',
                                    appStoreIntegrationId: 'google',
                                    buildIdentifier: '263',
                                    hash: 'EoC1sXC38yCv2FysjX3xoT8joUE=',
                                    integrationId: 'bitrise',
                                },
                            ],
                            regressionBuildInfo: {
                                associatedAppStoreBuild: {
                                    betaBuildDetails: { status: 'completed', userFraction: 0 },
                                    buildNumber: '27500',
                                    id: '27500',
                                    integrationId: 'google-beta',
                                    isProcessed: true,
                                    status: 'completed',
                                    type: 'android-aab',
                                    uploadedAt: '2022-03-15T18:11:14.055381Z',
                                    userFraction: 0,
                                },
                                ciBuild: {
                                    artifacts: null,
                                    branch: 'release-1.25.0',
                                    buildFinishedAt: '2022-03-15T18:09:36Z',
                                    buildHash: '5dGgafjgm3ECgAvSmUmyok8bFZY=',
                                    buildIdentifier: '275',
                                    buildStartedAt: '2022-03-15T18:06:21Z',
                                    buildStatus: 'success',
                                    buildUrl: 'https://app.bitrise.io/build/bc829153-e305-4058-bf4c-9f9d04fae7a9',
                                    commit: '8377f75e11ef0544ac5d7b138f8ec1af913203be',
                                    commitAuthor: 'runwayhelperdev[bot]',
                                    commitMessage:
                                        'Bumping version to 1.25.0 (#41)\n\nCo-authored-by: runwayhelperdev[bot] \u003c68658124+runwayhelperdev[bot]@users.noreply.github.com\u003e',
                                    integrationId: 'bitrise',
                                    providerBuildStatusString: 'Success',
                                    workflowRunId: 'bc829153-e305-4058-bf4c-9f9d04fae7a9',
                                },
                            },
                            status: 'inProgress',
                            testItems: [
                                {
                                    approverGroups: ['pilot'],
                                    comment: null,
                                    description: 'And make sure to do it in an efficient way',
                                    id: 'c4tuob32dnc706n8d30g',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: null,
                                    statuses: ['inProgress', 'blocked', 'failed', 'passed'],
                                    title: 'Run full checkout flow',
                                    type: 'multi',
                                    owners: ['ssullenberger@demo.com'],
                                },
                            ],
                            nonHistoricalTestRunsCompletionFraction: 0.95,
                            nonHistoricalTestRunsStatusesTotalCount: 30,
                            testRuns: [
                                {
                                    completedAt: null,
                                    completedCount: 10,
                                    createdAt: '2022-02-17T23:48:12Z',
                                    description: '',
                                    id: '1',
                                    integrationId: 'testrail',
                                    isInProgress: true,
                                    name: 'Test run',
                                    statuses: [
                                        { color: '6EC78B', count: 10, displayableName: 'Passed', fraction: 1 },
                                        { color: '595959', count: 1, displayableName: 'Blocked', fraction: 0 },
                                        { color: 'F5F5F5', count: 5, displayableName: 'Untested', fraction: 0 },
                                        { color: 'F4AD22', count: 1, displayableName: 'Retest', fraction: 0 },
                                        { color: 'EF2D56', count: 1, displayableName: 'Failed', fraction: 0 },
                                    ],
                                    totalCount: 18,
                                    updatedAt: '2022-02-17T23:52:10Z',
                                    url: 'https://demo.testrail.io',
                                    completionFraction: 0.55,
                                },
                            ],
                            updatedAt: '2022-03-23T20:27:22.163903405Z',
                        },
                        groupIndex: 1,
                        orderIndex: 1,
                        checklistItems: [],
                        automationRuns: androidStepAutomations['regressionTesting'],
                    },
                    {
                        id: '1.0.0#betaTesting',
                        type: 'betaTesting',
                        description: 'Beta testing',
                        status: 'pending',
                        statusReasonString: 'Beta soak in progress. Waiting for manual approval.',
                        blockingConfig: 'Blocking',
                        data: {
                            betaBuilds: [
                                {
                                    betaGroups: [],
                                    betaStatus: 'testing',
                                    betaTesters: [],
                                    betaTestingNotes: [
                                        {
                                            displayLocale: 'American English',
                                            id: '1.25.0:en-US',
                                            locale: 'en-US',
                                            notes: 'Preparing to release redesign of product page, redesign of checkout.',
                                        },
                                        {
                                            displayLocale: 'Australian English',
                                            id: '1.25.0:en-AU',
                                            locale: 'en-AU',
                                            notes: 'Preparing to release redesign of product page, redesign of checkout.',
                                        },
                                        {
                                            displayLocale: 'Latin American Spanish',
                                            id: '1.25.0:es-419',
                                            locale: 'es-419',
                                            notes: 'Preparing to release redesign of product page, redesign of checkout.',
                                        },
                                        {
                                            displayLocale: 'European Spanish',
                                            id: '1.25.0:es-ES',
                                            locale: 'es-ES',
                                            notes: 'Preparing to release redesign of product page, redesign of checkout.',
                                        },
                                    ],
                                    buildId: '27500',
                                    appStoreBuild: {
                                        appStoreBuild: {
                                            buildNumber: '27500',
                                            id: '27500',
                                            integrationId: 'google-beta',
                                            isProcessed: true,
                                            status: 'completed',
                                            type: 'android-aab',
                                            uploadedAt: '2022-03-15T18:11:14.055381Z',
                                        },
                                        associatedCIBuild: {
                                            branch: 'release-1.25.0',
                                            buildFinishedAt: '2022-03-15T18:09:36Z',
                                            buildHash: '5dGgafjgm3ECgAvSmUmyok8bFZY=',
                                            buildIdentifier: '275',
                                            buildStartedAt: '2022-03-15T18:06:21Z',
                                            buildStatus: 'success',
                                            buildUrl: 'https://app.bitrise.io/build/123',
                                            commit: '8377f75e11ef0544ac5d7b138f8ec1af913203be',
                                            commitAuthor: null,
                                            commitMessage:
                                                'Bumping version (#41)\n\nCo-authored-by: runwayhelperdev[bot] \u003c68658124+runwayhelperdev[bot]@users.noreply.github.com\u003e',
                                            integrationId: 'bitrise',
                                            providerBuildStatusString: 'Success',
                                            workflowRunId: 'bc829153-e305-4058-bf4c-9f9d04fae7a9',
                                        },
                                        stabilityInfos: {},
                                    },
                                    betaBuildDetails: { status: 'completed', userFraction: 0 },
                                    isConsideredExternal: false,
                                    isLatestBuild: true,
                                    isSelectedBuild: true,
                                    metrics: { numInstalls: 0 },
                                    selectedReason: 'autoRunwaySelection',
                                },
                                {
                                    betaGroups: [],
                                    betaStatus: 'not-testing',
                                    betaTesters: [],
                                    betaTestingNotes: null,
                                    buildId: '27100',
                                    appStoreBuild: {
                                        associatedCIBuild: {
                                            branch: 'release-1.25.0',
                                            buildFinishedAt: '2022-03-15T15:53:31Z',
                                            buildHash: 'v_LYY2Q_tHP__uFMuoahvSvrjbs=',
                                            buildIdentifier: '271',
                                            buildStartedAt: '2022-03-15T15:50:06Z',
                                            buildStatus: 'success',
                                            buildUrl: 'https://app.bitrise.io',
                                            commit: '8377f75e11ef0544ac5d7b138f8ec1af913203be',
                                            commitAuthor: null,
                                            commitMessage:
                                                'Bumping version to 1.25.0 (#41)\n\nCo-authored-by: runwayhelperdev[bot] \u003c68658124+runwayhelperdev[bot]@users.noreply.github.com\u003e',
                                            integrationId: 'bitrise',
                                            providerBuildStatusString: 'Success',
                                            workflowRunId: '459e9bfc-b185-4acf-b95f-caa50d70da40',
                                        },
                                        appStoreBuild: {
                                            buildNumber: '27100',
                                            id: '27100',
                                            integrationId: 'google-beta',
                                            isProcessed: true,
                                            status: null,
                                            type: 'android-aab',
                                            uploadedAt: '2022-03-15T15:53:26.217791Z',
                                        },
                                        stabilityInfos: {},
                                    },
                                    betaBuildDetails: { status: null, userFraction: 0 },
                                    isConsideredExternal: false,
                                    isLatestBuild: false,
                                    isSelectedBuild: false,
                                    metrics: { numInstalls: 0 },
                                    selectedReason: '',
                                },
                                {
                                    betaGroups: [],
                                    betaStatus: 'not-testing',
                                    betaTesters: [],
                                    betaTestingNotes: null,
                                    buildId: '26600',
                                    appStoreBuild: {
                                        appStoreBuild: {
                                            buildNumber: '26600',
                                            id: '26600',
                                            integrationId: 'google-beta',
                                            isProcessed: true,
                                            status: null,
                                            type: 'android-aab',
                                            uploadedAt: '2022-02-22T15:10:44.431772-06:00',
                                        },
                                        associatedCIBuild: {
                                            branch: 'release-1.25.0',
                                            buildFinishedAt: '2022-02-22T21:09:21Z',
                                            buildHash: 'xIF7XbFDz_ILtKZjP3TOnVythwo=',
                                            buildIdentifier: '266',
                                            buildStartedAt: '2022-02-22T21:05:24Z',
                                            buildStatus: 'success',
                                            buildUrl: 'https://app.bitrise.io',
                                            commit: '8377f75e11ef0544ac5d7b138f8ec1af913203be',
                                            commitAuthor: null,
                                            commitMessage:
                                                'Bumping version to 1.25.0 (#41)\n\nCo-authored-by: runwayhelperdev[bot] \u003c68658124+runwayhelperdev[bot]@users.noreply.github.com\u003e',
                                            integrationId: 'bitrise',
                                            providerBuildStatusString: 'Success',
                                            workflowRunId: '339716b3-ad88-417a-a16c-6b291ed196db',
                                        },
                                        stabilityInfos: {},
                                    },
                                    betaBuildDetails: { status: null, userFraction: 0 },
                                    isConsideredExternal: false,
                                    isLatestBuild: false,
                                    isSelectedBuild: false,
                                    metrics: { numInstalls: 0 },
                                    selectedReason: '',
                                },
                                {
                                    betaGroups: [],
                                    betaStatus: 'not-testing',
                                    betaTesters: [],
                                    betaTestingNotes: null,
                                    buildId: '26300',
                                    appStoreBuild: {
                                        appStoreBuild: {
                                            buildNumber: '26300',
                                            id: '26300',
                                            integrationId: 'google-beta',
                                            isProcessed: true,
                                            status: null,
                                            type: 'android-aab',
                                            uploadedAt: '2022-02-14T18:29:27.228176572Z',
                                        },
                                        associatedCIBuild: {
                                            branch: 'release-1.25.0',
                                            buildFinishedAt: '2022-02-14T18:29:57Z',
                                            buildHash: 'EoC1sXC38yCv2FysjX3xoT8joUE=',
                                            buildIdentifier: '263',
                                            buildStartedAt: '2022-02-14T18:26:48Z',
                                            buildStatus: 'success',
                                            buildUrl: 'https://app.bitrise.io',
                                            commit: '2a9abf9fb4eb2355dfd1600b622ea4f63ee4405d',
                                            commitAuthor: null,
                                            commitMessage:
                                                'Bumping version to 1.24.0 (#40)\n\nCo-authored-by: runwayhelperdev[bot] \u003c68658124+runwayhelperdev[bot]@users.noreply.github.com\u003e',
                                            integrationId: 'bitrise',
                                            providerBuildStatusString: 'Success',
                                            workflowRunId: 'c8bc9aa2-864f-46a0-99c8-c38fff620763',
                                        },
                                        stabilityInfos: {},
                                    },
                                    betaBuildDetails: { status: null, userFraction: 0 },
                                    isConsideredExternal: false,
                                    isLatestBuild: false,
                                    isSelectedBuild: false,
                                    metrics: { numInstalls: 0 },
                                    selectedReason: '',
                                },
                            ],
                            betaGroups: [
                                {
                                    id: 'windock-testing-group@googlegroups.com',
                                    isExternalGroup: true,
                                    name: 'windock-testing-group@googlegroups.com',
                                    testers: [],
                                },
                            ],
                            soakDidFinishEarly: false,
                            soakFinishedAt: null,
                            soakStartedAt: '2022-03-15T18:11:14.055381Z',
                            testers: null,
                        },
                        groupIndex: 1,
                        orderIndex: 2,
                        checklistItems: [],
                        automationRuns: androidStepAutomations['betaTesting'],
                    },
                    {
                        id: '1.0.0#screenshots',
                        type: 'screenshots',
                        description: 'Screenshots',
                        status: 'pending',
                        statusReasonString: 'Approval required.',
                        blockingConfig: 'Blocking',
                        data: {
                            isApproved: false,
                            requiresApproval: true,
                            screenshotLocalizations: [
                                {
                                    displayLocale: 'American English',
                                    locale: 'en-US',
                                    screenshotSets: [
                                        {
                                            screenshotDisplayType: 'phoneScreenshots',
                                            screenshots: [
                                                {
                                                    height: 1562,
                                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-Android-1.png',
                                                    width: 2778,
                                                },
                                                {
                                                    height: 1562,
                                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-Android-2.png',
                                                    width: 2778,
                                                },
                                            ],
                                        },
                                    ],
                                },
                                {
                                    displayLocale: 'European Spanish',
                                    locale: 'es-ES',
                                    screenshotSets: [
                                        {
                                            screenshotDisplayType: 'phoneScreenshots',
                                            screenshots: [
                                                {
                                                    height: 1562,
                                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-Android-1.png',
                                                    width: 2778,
                                                },
                                                {
                                                    height: 1562,
                                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-Android-2.png',
                                                    width: 2778,
                                                },
                                            ],
                                        },
                                    ],
                                },
                            ],
                            storeUrl: 'https://play.google.com/console',
                        },
                        groupIndex: 2,
                        orderIndex: 0,
                        checklistItems: [
                            {
                                id: 'c03218kcu89v1j3i28cg',
                                title: 'Upload screenshots to Google play per the guidelines',
                                description: null,
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'screenshots',
                                approverGroups: ['design'],
                                releaseTypes: 'all',
                                notifyEnabled: false,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: androidStepAutomations['screenshots'],
                    },
                    {
                        id: '1.0.0#metadata',
                        type: 'metadata',
                        description: 'Metadata',
                        status: 'pending',
                        statusReasonString: 'Waiting for manual approval',
                        blockingConfig: 'Blocking',
                        data: {
                            isApproved: false,
                            localizations: [
                                {
                                    description: null,
                                    displayLocale: 'American English',
                                    id: '1.25.0:en-US',
                                    keywords: null,
                                    locale: 'en-US',
                                    promotionalText: null,
                                    status: 'ready',
                                    whatsNew:
                                        "Bug fixes and improvements... we're always working hard to build a better app for you!",
                                },
                                {
                                    description: null,
                                    displayLocale: 'European Spanish',
                                    id: '1.25.0:es-ES',
                                    keywords: null,
                                    locale: 'es-ES',
                                    promotionalText: null,
                                    status: 'ready',
                                    whatsNew:
                                        "Bug fixes and improvements... we're always working hard to build a better app for you!",
                                },
                            ],
                            requiresApproval: true,
                            storeUrl: 'https://play.google.com/console',
                        },
                        groupIndex: 2,
                        orderIndex: 1,
                        checklistItems: [],
                        automationRuns: androidStepAutomations['metadata'],
                    },
                    {
                        id: '1.0.0#approvals',
                        type: 'approvals',
                        description: 'Approvals',
                        status: 'pending',
                        statusReasonString: 'There are 2 items that still require approval.',
                        blockingConfig: 'Blocking',
                        data: {
                            approvalItems: [
                                {
                                    approverGroups: ['pm'],
                                    comment: null,
                                    description: null,
                                    id: 'c8to4mu9f6i9qtljdkjg',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: {
                                        createdAt: '2022-03-23T20:26:38.120635796Z',
                                        createdBy: 'aearhart@demo.com',
                                        status: 'approved',
                                    },
                                    statuses: ['inProgress', 'blocked', 'rejected', 'approved'],
                                    title: 'Approval from Engagement team',
                                    type: 'multi',
                                    owners: ['aearhart@demo.com'],
                                },
                                {
                                    approverGroups: ['engineer'],
                                    comment: null,
                                    description: null,
                                    id: 'c8to4q69f6i9qtljdkk0',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: null,
                                    statuses: ['inProgress', 'blocked', 'rejected', 'approved'],
                                    title: 'Approval from Acquisitions team',
                                    type: 'multi',
                                    owners: ['narmstrong@demo.com', 'ssullenberger@demo.com'],
                                },
                                {
                                    approverGroups: ['pm'],
                                    comment: null,
                                    description: null,
                                    id: 'c8to4t69f6i9qtljdkkg',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: null,
                                    statuses: ['inProgress', 'blocked', 'rejected', 'approved'],
                                    title: 'Approval from Payments team',
                                    type: 'multi',
                                    owners: [],
                                    incompleteReminderEnabled: false,
                                },
                            ],
                        },
                        groupIndex: 3,
                        orderIndex: 0,
                        checklistItems: [],
                        automationRuns: androidStepAutomations['approvals'],
                    },
                    {
                        id: '1.0.0#submission',
                        type: 'submission',
                        description: 'Submission',
                        status: 'pending',
                        statusReasonString: 'Ready to submit.',
                        blockingConfig: 'Blocking',
                        data: {
                            appStoreData: {
                                allInfoPresentForSubmission: true,
                                integrationId: 'google',
                                phasedRelease: {
                                    isManuallyAcceleratedPhasedRollout: false,
                                    isRunwayAcceleratedRollout: false,
                                    isRunwayPaused: false,
                                    state: 'draft',
                                    userFraction: 0,
                                },
                                state: 'draft',
                            },
                            storeUrl: 'https://play.google.com/console',
                            submittedAt: null,
                            workflowData: null,
                        },
                        groupIndex: 4,
                        orderIndex: 0,
                        checklistItems: [
                            {
                                id: 'c5n2h7ln86bo9gnibnr0',
                                title: 'Thing to do before submit',
                                description: 'Do this!',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'submission',
                                approverGroups: ['approver'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'c8al5bdn86bs44bjrpd0',
                                title: 'Another thing to remember before submitting',
                                description: 'All this stuff',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'submission',
                                approverGroups: ['pilot', 'pm'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'c8e0r55n86boufv00ong',
                                title: 'Important Pre-submit step',
                                description: 'Remember to do this before submitting.',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'submission',
                                approverGroups: ['pilot', 'pm'],
                                releaseTypes: 'all',
                                notifyEnabled: false,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: androidStepAutomations['submission'],
                    },
                    {
                        id: '1.0.0#storeReview',
                        type: 'storeReview',
                        description: 'App store review',
                        status: 'inactive',
                        statusReasonString: 'Waiting for submission.',
                        blockingConfig: 'Blocking',
                        data: {
                            appStoreData: {
                                allInfoPresentForSubmission: true,
                                integrationId: 'google',
                                phasedRelease: {
                                    isManuallyAcceleratedPhasedRollout: false,
                                    isRunwayAcceleratedRollout: false,
                                    isRunwayPaused: false,
                                    state: 'draft',
                                    userFraction: 0,
                                },
                                state: 'draft',
                            },
                            approvedAt: null,
                            devRejectedAt: null,
                            startedReviewAt: null,
                            storeRejectedAt: null,
                            storeUrl: 'https://play.google.com/console',
                            submittedAt: null,
                        },
                        groupIndex: 4,
                        orderIndex: 1,
                        checklistItems: [
                            {
                                id: 'c7oomvdn86bpp9lejr1g',
                                title: 'Release via managed publishing if needed',
                                description: null,
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'storeReview',
                                approverGroups: ['pilot'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'c59kpldachdcretdibcg',
                                title: 'Many different users can deal with this',
                                description: 'engineers, qa and pms should be able to approve this',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'storeReview',
                                approverGroups: ['engineer', 'qa', 'pm'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: androidStepAutomations['storeReview'],
                    },
                    {
                        id: '1.0.0#takeoff',
                        type: 'takeoff',
                        description: 'Release',
                        status: 'inactive',
                        statusReasonString: 'Waiting for submission.',
                        blockingConfig: 'Blocking',
                        data: {
                            appStoreData: {
                                allInfoPresentForSubmission: true,
                                integrationId: 'google',
                                phasedRelease: {
                                    isManuallyAcceleratedPhasedRollout: false,
                                    isRunwayAcceleratedRollout: false,
                                    isRunwayPaused: false,
                                    state: 'draft',
                                    userFraction: 0,
                                },
                                state: 'draft',
                            },
                            appStoreUrl: 'https://play.google.com/store/apps/details?id=com.runway.example',
                            storeUrl: 'https://play.google.com/console',
                            tagData: {
                                codeDiffUrl: 'https://github.com',
                                expectedReleaseTagName: 'v1.25.0',
                                releaseTag: null,
                                releaseTagState: 'notTaggable',
                                taggableCommits: [],
                            },
                        },
                        groupIndex: 4,
                        orderIndex: 2,
                        checklistItems: [
                            {
                                id: 'bvv18nkfoo7cohhp7ju0',
                                title: 'Send announcement email to team@ distribution list',
                                description: null,
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'takeoff',
                                approverGroups: ['pilot'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: androidStepAutomations['takeoff'],
                    },
                ]
            :   [
                    {
                        id: '1.0.0#kickoff',
                        type: 'kickoff',
                        description: 'Kickoff',
                        status: 'ready',
                        statusReasonString: 'Kicked off!',
                        blockingConfig: 'Blocking',
                        data: {
                            appUsesStaticReleaseBranch: true,
                            bumpVersionStatus: 'detected',
                            canPerformBumpVersion: true,
                            canPerformCodePromotion: true,
                            codePromotionPullRequest: {
                                id: '1234',
                                number: 119,
                                status: 'merged',
                                title: 'Merge development -\u003e main',
                                url: 'https://github.com/runway/demo-ios/pull/119',
                            },
                            codePromotionStatus: 'detected',
                            doesReleaseBranchExist: true,
                            hasPerformedAnyCodePromotion: true,
                            hotfixBranchingBase: null,
                            previousReleaseTag: 'v1.0.0',
                            previousReleaseVersion: '1.0.0',
                            previousTaggedReleaseTag: 'v1.0.0',
                            previousTaggedReleaseTagVersionsBehind: 0,
                            releaseBranch: 'main',
                            releaseBranchCodeVersionData: {
                                branchName: 'main',
                                bumpVersionPullRequest: null,
                                bumpVersionStatus: 'detected',
                                canPerformBumpVersion: false,
                                currentCodeVersionExternalString: '1.0.0',
                                targetCodeVersionExternalString: '1.0.0',
                                targetCodeVersionInternalString: '1.0.0',
                            },
                            workingBranchCodeVersionData: {
                                branchName: 'master',
                                bumpVersionPullRequest: {
                                    id: 'PR_123',
                                    number: 96,
                                    pullRequestType: 'BumpVersion',
                                    status: 'merged',
                                    title: 'Bumping version to 1.0.0',
                                    url: 'https://github.com/runway-org/fake-app/pull/96',
                                },
                                bumpVersionStatus: 'merged',
                                canPerformBumpVersion: false,
                                currentCodeVersionExternalString: '1.0.0',
                                targetCodeVersionExternalString: '1.0.0',
                                targetCodeVersionInternalString: '1.0.0',
                            },
                            releaseBranchDiffUrl: 'https://github.com/runway/demo-ios/compare',
                            releaseBranchUrl: 'https://github.com/runway/demo-ios/tree/main',
                            resolvedFeatureAffiliations: [
                                {
                                    affiliationType: 'jira-fix-version',
                                    integrationId: 'jira',
                                    resolvedString: '1.0.0',
                                },
                                {
                                    affiliationType: 'jira-label',
                                    integrationId: 'jira',
                                    resolvedString: 'release-1.0.0',
                                },
                            ],
                            workingBranchDiffUrl: 'https://github.com/runway/demo-ios/compare',
                        },
                        groupIndex: 0,
                        orderIndex: 0,
                        checklistItems: [
                            {
                                id: 'abc',
                                title: 'Pre-kickoff reminders',
                                description: 'Description   \u003cb\u003eyo\u003c/b\u003e !!',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'kickoff',
                                approverGroups: ['pilot'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: {
                                    status: 'approved',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'def',
                                title: 'Send out reminder for upcoming release deadlines',
                                description: 'Some detail',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'kickoff',
                                approverGroups: ['pilot'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: {
                                    status: 'approved',
                                    createdAt: '2022-03-03T18:03:32.236911544Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                comment: {
                                    comment: 'Reminder sent via email',
                                    createdAt: '2022-03-03T18:00:32.236911544Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'jkl',
                                title: 'Verify versioning with product team',
                                description: null,
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'kickoff',
                                approverGroups: [],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: {
                                    status: 'approved',
                                    createdAt: '2022-03-03T18:03:33.133516479Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: iosStepAutomations['kickoff'],
                    },
                    {
                        id: '1.0.0#featureReadiness',
                        type: 'featureReadiness',
                        description: 'Feature readiness',
                        status: 'pending',
                        statusReasonString: 'Some work for this release is not yet complete.',
                        blockingConfig: 'Blocking',
                        data: { items: null, percentComplete: 0, workingBranchPercentComplete: null },
                        groupIndex: 0,
                        orderIndex: 1,
                        checklistItems: [
                            {
                                id: 'abc',
                                title: 'Verify production feature flags are correct for this release',
                                description: null,
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'featureReadiness',
                                approverGroups: ['engineer'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: {
                                    status: 'approved',
                                    createdAt: '2022-03-15T22:54:38.462070296Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'def',
                                title: 'Update CMS version flags for this release',
                                description: '',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'featureReadiness',
                                approverGroups: [],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: {
                                    status: 'approved',
                                    createdAt: '2022-03-15T22:54:39.472656772Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: iosStepAutomations['featureReadiness'],
                    },
                    {
                        id: '1.0.0#releaseCandidate',
                        type: 'releaseCandidate',
                        description: 'CI',
                        status: 'ready',
                        statusReasonString: 'CI build succeeded.',
                        blockingConfig: 'Blocking',
                        data: {
                            builds: [
                                {
                                    associatedAppStoreBuild: {
                                        betaBuildDetails: {
                                            autoNotifyEnabled: true,
                                            externalBuildState: 'IN_BETA_TESTING',
                                            internalBuildState: 'IN_BETA_TESTING',
                                        },
                                        buildNumber: '298',
                                        id: '123',
                                        integrationId: 'apple',
                                        isProcessed: true,
                                        processingState: 'VALID',
                                        type: 'ios-binary',
                                        uploadedAt: '2022-03-15T12:31:21-07:00',
                                    },
                                    ciBuild: {
                                        artifacts: [],
                                        branch: 'main',
                                        buildFinishedAt: '2022-03-21T17:32:25Z',
                                        buildHash: 'zcebLLhLwk8m-GonPSg84G6omXw=',
                                        buildIdentifier: '298',
                                        buildStartedAt: '2022-03-21T17:23:53Z',
                                        buildStatus: 'success',
                                        buildUrl: 'https://app.bitrise.io/build/abc',
                                        commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                        commitAuthor: 'runway-github[bot]',
                                        commitMessage:
                                            'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main`.',
                                        integrationId: 'bitrise',
                                        providerBuildStatusString: 'Success',
                                        workflowRunId: 'abc',
                                    },
                                    isSelectedBuild: true,
                                    selectedReason: 'autoRunwaySelection',
                                },
                                {
                                    associatedAppStoreBuild: {
                                        betaBuildDetails: {
                                            autoNotifyEnabled: true,
                                            externalBuildState: 'IN_BETA_TESTING',
                                            internalBuildState: 'IN_BETA_TESTING',
                                        },
                                        buildNumber: '297',
                                        id: '123',
                                        integrationId: 'apple',
                                        isProcessed: true,
                                        processingState: 'VALID',
                                        type: 'ios-binary',
                                        uploadedAt: '2022-03-15T12:31:21-07:00',
                                    },
                                    ciBuild: {
                                        artifacts: [
                                            {
                                                downloadUrl: 'https://demo.runway.team',
                                                fileName: 'app.dSYM.zip',
                                                size: 16,
                                            },
                                            {
                                                downloadUrl: 'https://demo.runway.team',
                                                fileName: 'app.ipa',
                                                size: 139,
                                            },
                                        ],
                                        branch: 'main',
                                        buildFinishedAt: '2022-03-15T19:28:41Z',
                                        buildHash: '123=',
                                        buildIdentifier: '297',
                                        buildStartedAt: '2022-03-15T19:17:48Z',
                                        buildStatus: 'success',
                                        buildUrl: 'https://app.bitrise.io/build/123',
                                        commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                        commitAuthor: 'runway-github[bot]',
                                        commitMessage:
                                            'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main`',
                                        integrationId: 'bitrise',
                                        providerBuildStatusString: 'Success',
                                        workflowRunId: '123',
                                    },
                                    isSelectedBuild: false,
                                    selectedReason: 'autoRunwaySelection',
                                },
                                {
                                    associatedAppStoreBuild: {
                                        betaBuildDetails: {
                                            autoNotifyEnabled: true,
                                            externalBuildState: 'IN_BETA_TESTING',
                                            internalBuildState: 'IN_BETA_TESTING',
                                        },
                                        buildNumber: '296',
                                        id: 'abc',
                                        integrationId: 'apple',
                                        isProcessed: true,
                                        processingState: 'VALID',
                                        type: 'ios-binary',
                                        uploadedAt: '2022-03-15T11:50:03-07:00',
                                    },
                                    ciBuild: {
                                        artifacts: [
                                            {
                                                downloadUrl: 'https://demo.runway.team',
                                                fileName: 'app.ipa',
                                                size: 139,
                                            },
                                            {
                                                downloadUrl: 'https://demo.runway.team',
                                                fileName: 'app.dSYM.zip',
                                                size: 16,
                                            },
                                        ],
                                        branch: 'main',
                                        buildFinishedAt: '2022-03-15T18:46:11Z',
                                        buildHash: 'abcd',
                                        buildIdentifier: '296',
                                        buildStartedAt: '2022-03-15T18:35:29Z',
                                        buildStatus: 'success',
                                        buildUrl: 'https://app.bitrise.io/build/abc',
                                        commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                        commitAuthor: 'runway-github[bot]',
                                        commitMessage:
                                            'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main`.',
                                        integrationId: 'bitrise',
                                        providerBuildStatusString: 'Success',
                                        workflowRunId: 'abc-abc-abc',
                                    },
                                    isSelectedBuild: false,
                                    selectedReason: 'autoRunwaySelection',
                                },
                                {
                                    associatedAppStoreBuild: null,
                                    ciBuild: {
                                        artifacts: [],
                                        branch: 'main',
                                        buildFinishedAt: '2022-03-15T18:35:29Z',
                                        buildHash: 'def',
                                        buildIdentifier: '295',
                                        buildStartedAt: '2022-03-15T18:33:17Z',
                                        buildStatus: 'stopped',
                                        buildUrl: 'https://app.bitrise.io/build/def',
                                        commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                        commitAuthor: 'runway-github[bot]',
                                        commitMessage:
                                            'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main`.',
                                        integrationId: 'bitrise',
                                        providerBuildStatusString: 'Stopped',
                                        workflowRunId: '123-123-123',
                                    },
                                    isSelectedBuild: false,
                                    selectedReason: 'autoRunwaySelection',
                                },
                                {
                                    associatedAppStoreBuild: {
                                        betaBuildDetails: {
                                            autoNotifyEnabled: true,
                                            externalBuildState: 'IN_BETA_TESTING',
                                            internalBuildState: 'IN_BETA_TESTING',
                                        },
                                        buildNumber: '290',
                                        id: 'bef',
                                        integrationId: 'apple',
                                        isProcessed: true,
                                        processingState: 'VALID',
                                        type: 'ios-binary',
                                        uploadedAt: '2022-03-11T12:08:29-08:00',
                                    },
                                    ciBuild: {
                                        artifacts: [
                                            {
                                                downloadUrl: 'https://demo.runway.team',
                                                fileName: 'app.dSYM.zip',
                                                size: 16,
                                            },
                                            {
                                                downloadUrl: 'https://demo.runway.team',
                                                fileName: 'app.ipa',
                                                size: 139,
                                            },
                                        ],
                                        branch: 'main',
                                        buildFinishedAt: '2022-03-11T20:05:46Z',
                                        buildHash: 'cya',
                                        buildIdentifier: '290',
                                        buildStartedAt: '2022-03-11T19:55:17Z',
                                        buildStatus: 'success',
                                        buildUrl: 'https://app.bitrise.io/build/123',
                                        commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                        commitAuthor: 'runway-github[bot]',
                                        commitMessage:
                                            'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main` for release.',
                                        integrationId: 'bitrise',
                                        providerBuildStatusString: 'Success',
                                        workflowRunId: 'cya-cya',
                                    },
                                    isSelectedBuild: false,
                                    selectedReason: 'autoRunwaySelection',
                                },
                                {
                                    associatedAppStoreBuild: {
                                        betaBuildDetails: {
                                            autoNotifyEnabled: true,
                                            externalBuildState: 'IN_BETA_TESTING',
                                            internalBuildState: 'IN_BETA_TESTING',
                                        },
                                        buildNumber: '289',
                                        id: '123',
                                        integrationId: 'apple',
                                        isProcessed: true,
                                        processingState: 'VALID',
                                        type: 'ios-binary',
                                        uploadedAt: '2022-03-10T08:20:05-08:00',
                                    },
                                    ciBuild: {
                                        artifacts: [
                                            {
                                                downloadUrl: 'https://demo.runway.team',
                                                fileName: 'app.dSYM.zip',
                                                size: 16,
                                            },
                                            {
                                                downloadUrl: 'https://demo.runway.team',
                                                fileName: 'app.ipa',
                                                size: 139,
                                            },
                                        ],
                                        branch: 'main',
                                        buildFinishedAt: '2022-03-10T16:16:53Z',
                                        buildHash: '123-abc',
                                        buildIdentifier: '289',
                                        buildStartedAt: '2022-03-10T16:06:14Z',
                                        buildStatus: 'success',
                                        buildUrl: 'https://app.bitrise.io/build/123-abc',
                                        commit: '0facaf1063bfa427b4bb1b5dba5b35d662c3bf45',
                                        commitAuthor: 'runway-github[bot]',
                                        commitMessage:
                                            'Merge pull request #118 from demo/development\n\nRunway promoted branch `development` to `main`.',
                                        integrationId: 'bitrise',
                                        providerBuildStatusString: 'Success',
                                        workflowRunId: '123-abc',
                                    },
                                    isSelectedBuild: false,
                                    selectedReason: 'autoRunwaySelection',
                                },
                                {
                                    associatedAppStoreBuild: {
                                        betaBuildDetails: {
                                            autoNotifyEnabled: true,
                                            externalBuildState: 'IN_BETA_TESTING',
                                            internalBuildState: 'IN_BETA_TESTING',
                                        },
                                        buildNumber: '284',
                                        id: '123',
                                        integrationId: 'apple',
                                        isProcessed: true,
                                        processingState: 'VALID',
                                        type: 'ios-binary',
                                        uploadedAt: '2022-03-03T10:09:08-08:00',
                                    },
                                    ciBuild: {
                                        artifacts: [
                                            {
                                                downloadUrl: 'https://demo.runway.team',
                                                fileName: 'tab.ipa',
                                                size: 139,
                                            },
                                            {
                                                downloadUrl: 'https://demo.runway.team',
                                                fileName: 'tab.dSYM.zip',
                                                size: 16,
                                            },
                                        ],
                                        branch: 'main',
                                        buildFinishedAt: '2022-03-03T18:05:50Z',
                                        buildHash: 'ux-OXdNnKuSGcQGoUxWVzOmXmBk=',
                                        buildIdentifier: '284',
                                        buildStartedAt: '2022-03-03T17:55:15Z',
                                        buildStatus: 'success',
                                        buildUrl: 'https://app.bitrise.io/build/123',
                                        commit: '0facaf1063bfa427b4bb1b5dba5b35d662c3bf45',
                                        commitAuthor: 'runway-github[bot]',
                                        commitMessage:
                                            'Merge pull request #118 from demo/development\n\nRunway promoted branch `development` to `main` for release 3.97.0.',
                                        integrationId: 'bitrise',
                                        providerBuildStatusString: 'Success',
                                        workflowRunId: 'abc-def-hij',
                                    },
                                    isSelectedBuild: false,
                                    selectedReason: 'autoRunwaySelection',
                                },
                            ],
                            workflowData: {
                                branch: 'main',
                                headCommit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                headCommitAuthor: 'runway-github[bot]',
                                headCommitMessage:
                                    'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main` for release 3.97.0.',
                                integrationType: 'bitrise',
                                workflowName: 'deploy',
                                workflowType: 'staging',
                                workflowUrl: 'https://app.bitrise.io/app/123/workflow_editor',
                            },
                        },
                        groupIndex: 1,
                        orderIndex: 0,
                        checklistItems: [],
                        automationRuns: iosStepAutomations['releaseCandidate'],
                    },
                    {
                        id: '1.0.0#regressionTesting',
                        type: 'regressionTesting',
                        description: 'Regression testing',
                        status: 'pending',
                        statusReasonString: 'Regression testing in progress...',
                        blockingConfig: 'Blocking',
                        data: {
                            availableBuilds: [
                                {
                                    appStoreBuildIdentifier: '297',
                                    appStoreIntegrationId: 'apple',
                                    buildIdentifier: '297',
                                    hash: 'A5LZuDfhn3e6WNweXBzV8AR1w6c=',
                                    integrationId: 'bitrise',
                                },
                                {
                                    appStoreBuildIdentifier: '296',
                                    appStoreIntegrationId: 'apple',
                                    buildIdentifier: '296',
                                    hash: '0qVMy0jBTbwzL5k67ZzU092K-wA=',
                                    integrationId: 'bitrise',
                                },
                                {
                                    appStoreBuildIdentifier: '290',
                                    appStoreIntegrationId: 'apple',
                                    buildIdentifier: '290',
                                    hash: 'cYAkkfz3H8wQ_0fBrtp05sJ6uMA=',
                                    integrationId: 'bitrise',
                                },
                                {
                                    appStoreBuildIdentifier: '289',
                                    appStoreIntegrationId: 'apple',
                                    buildIdentifier: '289',
                                    hash: '2LLiuHxPPvmLOnRjj8SzN86Gx54=',
                                    integrationId: 'bitrise',
                                },
                                {
                                    appStoreBuildIdentifier: '284',
                                    appStoreIntegrationId: 'apple',
                                    buildIdentifier: '284',
                                    hash: 'ux-OXdNnKuSGcQGoUxWVzOmXmBk=',
                                    integrationId: 'bitrise',
                                },
                            ],
                            regressionBuildInfo: {
                                associatedAppStoreBuild: {
                                    betaBuildDetails: {
                                        autoNotifyEnabled: true,
                                        externalBuildState: 'IN_BETA_TESTING',
                                        internalBuildState: 'IN_BETA_TESTING',
                                    },
                                    buildNumber: '297',
                                    id: 'fdeaddb9-87af-446b-baff-91eb85d1446c',
                                    integrationId: 'apple',
                                    isProcessed: true,
                                    processingState: 'VALID',
                                    type: 'ios-binary',
                                    uploadedAt: '2022-03-15T12:31:21-07:00',
                                },
                                ciBuild: {
                                    artifacts: null,
                                    branch: 'main',
                                    buildFinishedAt: '2022-03-15T19:28:41Z',
                                    buildHash: 'A5LZuDfhn3e6WNweXBzV8AR1w6c=',
                                    buildIdentifier: '297',
                                    buildStartedAt: '2022-03-15T19:17:48Z',
                                    buildStatus: 'success',
                                    buildUrl: 'https://app.bitrise.io/build/123',
                                    commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                    commitAuthor: 'runway-github[bot]',
                                    commitMessage:
                                        'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main` for release 3.97.0.',
                                    integrationId: 'bitrise',
                                    providerBuildStatusString: 'Success',
                                    workflowRunId: '0a65a37e-ba7d-45d7-bd3f-3ec0ba117bd8',
                                },
                            },
                            status: 'inProgress',
                            testItems: [
                                {
                                    approverGroups: ['qa'],
                                    comment: {
                                        comment: 'Tested sign in with google, github, apple, all look good',
                                        createdAt: '2022-02-27T23:00:20.886428625Z',
                                        createdBy: 'ssullenberger@demo.com',
                                    },
                                    description: null,
                                    id: 'c55n1kdachdaceuvmb00',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: {
                                        status: 'passed',
                                        createdAt: '2022-02-27T23:05:20.886428625Z',
                                        createdBy: 'ssullenberger@demo.com',
                                    },
                                    statuses: ['inProgress', 'blocked', 'failed', 'passed'],
                                    title: 'Test login flow',
                                    type: 'multi',
                                    owners: ['ssullenberger@demo.com'],
                                },
                                {
                                    approverGroups: ['qa'],
                                    comment: null,
                                    description: null,
                                    id: 'c8kfkuln86busgdit4ag',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: {
                                        status: 'inProgress',
                                        createdAt: '2022-02-27T23:05:20.886428625Z',
                                        createdBy: 'aearhart@demo.com',
                                    },
                                    statuses: ['inProgress', 'blocked', 'failed', 'passed'],
                                    title: 'Test "Add receipts" flow',
                                    type: 'multi',
                                    owners: ['narmstrong@demo.com'],
                                },
                                {
                                    approverGroups: ['qa'],
                                    comment: null,
                                    description: null,
                                    id: 'c55n1p5achdaceuvmb0g',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: {
                                        status: 'passed',
                                        createdAt: '2022-02-27T23:05:20.886428625Z',
                                        createdBy: 'aearhart@demo.com',
                                    },
                                    statuses: ['inProgress', 'blocked', 'failed', 'passed'],
                                    title: 'Test checkout flow',
                                    type: 'multi',
                                    owners: [],
                                    incompleteReminderEnabled: false,
                                },
                                {
                                    approverGroups: ['qa'],
                                    comment: null,
                                    description: null,
                                    id: 'c55n25lachdaceuvmb1g',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: null,
                                    statuses: ['inProgress', 'blocked', 'failed', 'passed'],
                                    title: 'Test "Share with friends" flow',
                                    type: 'multi',
                                    owners: [],
                                    incompleteReminderEnabled: false,
                                },
                                {
                                    approverGroups: ['qa'],
                                    comment: null,
                                    description: 'Remember to test this stuff right now',
                                    id: 'c8efiddn86bppsoltosg',
                                    notifyEnabled: false,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: {
                                        status: 'failed',
                                        createdAt: '2022-02-27T23:05:20.886428625Z',
                                        createdBy: 'aearhart@demo.com',
                                    },
                                    statuses: ['inProgress', 'blocked', 'failed', 'passed'],
                                    title: 'Another thing to test',
                                    type: 'multi',
                                    owners: [],
                                    incompleteReminderEnabled: false,
                                },
                            ],
                            nonHistoricalTestRunsCompletionFraction: 0.95,
                            nonHistoricalTestRunsStatusesTotalCount: 30,
                            testRuns: [
                                {
                                    completedAt: null,
                                    completedCount: 19,
                                    createdAt: '2022-02-17T23:48:12Z',
                                    description: '',
                                    id: '1',
                                    integrationId: 'testrail',
                                    isInProgress: true,
                                    name: 'Checkout Pathways',
                                    statuses: [
                                        { color: '6EC78B', count: 19, displayableName: 'Passed', fraction: 0.95 },
                                        { color: '595959', count: 0, displayableName: 'Blocked', fraction: 0 },
                                        { color: 'd9d9d9', count: 1, displayableName: 'Untested', fraction: 0.05 },
                                        { color: 'F4AD22', count: 0, displayableName: 'Retest', fraction: 0 },
                                        { color: 'EF2D56', count: 0, displayableName: 'Failed', fraction: 0 },
                                    ],
                                    totalCount: 20,
                                    updatedAt: '2022-02-17T23:52:10Z',
                                    url: 'https://demo.testrail.io',
                                    completionFraction: 0.95,
                                    isHistorical: false,
                                },
                                {
                                    completedAt: null,
                                    completedCount: 9,
                                    createdAt: '2022-02-17T23:48:12Z',
                                    description: '',
                                    id: '1',
                                    integrationId: 'testrail',
                                    isInProgress: true,
                                    name: 'Core Pathways',
                                    statuses: [
                                        { color: '6EC78B', count: 9, displayableName: 'Passed', fraction: 0.55 },
                                        { color: '595959', count: 1, displayableName: 'Blocked', fraction: 0.05 },
                                        { color: 'd9d9d9', count: 5, displayableName: 'Untested', fraction: 0.3 },
                                        { color: 'F4AD22', count: 1, displayableName: 'Retest', fraction: 0.05 },
                                        { color: 'EF2D56', count: 2, displayableName: 'Failed', fraction: 0.1 },
                                    ],
                                    totalCount: 18,
                                    updatedAt: '2022-02-17T23:52:10Z',
                                    url: 'https://demo.testrail.io',
                                    completionFraction: 0.55,
                                    isHistorical: false,
                                },
                                {
                                    completedAt: '2022-02-18T23:48:12Z',
                                    completedCount: 18,
                                    createdAt: '2022-02-17T23:48:12Z',
                                    description: '',
                                    id: '1',
                                    integrationId: 'testrail',
                                    isInProgress: false,
                                    name: 'Core pathways',
                                    statuses: [
                                        { color: '6EC78B', count: 10, displayableName: 'Passed', fraction: 1 },
                                        { color: '595959', count: 0, displayableName: 'Blocked', fraction: 0 },
                                        { color: 'd9d9d9', count: 0, displayableName: 'Untested', fraction: 0 },
                                        { color: 'F4AD22', count: 0, displayableName: 'Retest', fraction: 0 },
                                        { color: 'EF2D56', count: 0, displayableName: 'Failed', fraction: 0 },
                                    ],
                                    totalCount: 18,
                                    updatedAt: '2022-02-17T23:52:10Z',
                                    url: 'https://demo.testrail.io',
                                    completionFraction: 1,
                                    isHistorical: true,
                                },
                                {
                                    completedAt: '2022-02-18T23:48:12Z',
                                    completedCount: 18,
                                    createdAt: '2022-02-17T23:48:12Z',
                                    description: '',
                                    id: '1',
                                    integrationId: 'testrail',
                                    isInProgress: false,
                                    name: 'New user pathway',
                                    statuses: [
                                        { color: '6EC78B', count: 10, displayableName: 'Passed', fraction: 1 },
                                        { color: '595959', count: 0, displayableName: 'Blocked', fraction: 0 },
                                        { color: 'd9d9d9', count: 0, displayableName: 'Untested', fraction: 0 },
                                        { color: 'F4AD22', count: 0, displayableName: 'Retest', fraction: 0 },
                                        { color: 'EF2D56', count: 0, displayableName: 'Failed', fraction: 0 },
                                    ],
                                    totalCount: 18,
                                    updatedAt: '2022-02-17T23:52:10Z',
                                    url: 'https://demo.testrail.io',
                                    completionFraction: 1,
                                    isHistorical: true,
                                },
                            ],
                            updatedAt: '2022-03-15T18:57:32.629493-04:00',
                        },
                        groupIndex: 1,
                        orderIndex: 1,
                        checklistItems: [],
                        automationRuns: iosStepAutomations['regressionTesting'],
                    },
                    {
                        id: '1.0.0#betaTesting',
                        type: 'betaTesting',
                        description: 'Beta testing',
                        status: 'ready',
                        statusReasonString: 'Beta testing completed!',
                        blockingConfig: 'Blocking',
                        data: {
                            betaBuilds: [
                                {
                                    betaGroups: [
                                        '6c1c4439-16cb-49e8-b7c4-97d7c1f46a51',
                                        '7874adb9-9af0-427f-aabd-f7d36bed9049',
                                    ],
                                    betaStatus: 'testing',
                                    betaTesters: ['aearhart@demo.com', 'charles@demo.com', 'jimmy@demo.com'],
                                    betaTestingNotes: [
                                        {
                                            displayLocale: 'American English',
                                            id: 'cc6e2e20-0b62-441e-b755-594bed74b9e6',
                                            locale: 'en-US',
                                            notes: 'Test bug fixes on product page, checkout new design.',
                                        },
                                    ],
                                    buildId: '475bf507-c3c4-443f-9845-932f16748ec7',
                                    appStoreBuild: {
                                        appStoreBuild: {
                                            buildNumber: '298',
                                            id: '475bf507-c3c4-443f-9845-932f16748ec7',
                                            integrationId: 'apple-beta',
                                            isProcessed: true,
                                            processingState: 'VALID',
                                            type: 'ios-binary',
                                            uploadedAt: '2022-03-15T18:07:07-07:00',
                                        },
                                        associatedCIBuild: {
                                            branch: 'main',
                                            buildFinishedAt: '2022-03-15T18:46:11Z',
                                            buildHash: '0qVMy0jBTbwzL5k67ZzU092K-wA=',
                                            buildIdentifier: '298',
                                            buildStartedAt: '2022-03-15T18:35:29Z',
                                            buildStatus: 'success',
                                            buildUrl: 'https://app.bitrise.io/build/123',
                                            commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                            commitAuthor: null,
                                            commitMessage:
                                                'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main` for release 3.97.0.',
                                            integrationId: 'bitrise',
                                            providerBuildStatusString: 'Success',
                                            workflowRunId: '482375f7-5c67-46a0-b3f3-0e24907c2e3c',
                                        },
                                        stabilityInfos: {},
                                    },
                                    betaBuildDetails: {
                                        autoNotifyEnabled: true,
                                        externalBuildState: 'IN_BETA_TESTING',
                                        internalBuildState: 'IN_BETA_TESTING',
                                    },
                                    isConsideredExternal: true,
                                    isLatestBuild: true,
                                    isSelectedBuild: true,
                                    metrics: { numInstalls: 0 },
                                    selectedReason: 'autoRunwaySelection',
                                },
                                {
                                    betaGroups: [
                                        '6c1c4439-16cb-49e8-b7c4-97d7c1f46a51',
                                        '7874adb9-9af0-427f-aabd-f7d36bed9049',
                                    ],
                                    betaStatus: 'testing',
                                    betaTesters: [],
                                    betaTestingNotes: [
                                        {
                                            displayLocale: 'American English',
                                            id: 'ea1599fb-b230-4966-b044-5f2ac9be42ab',
                                            locale: 'en-US',
                                            notes: '',
                                        },
                                    ],
                                    betaBuildDetails: {
                                        autoNotifyEnabled: true,
                                        externalBuildState: 'IN_BETA_TESTING',
                                        internalBuildState: 'IN_BETA_TESTING',
                                    },
                                    appStoreBuild: {
                                        appStoreBuild: {
                                            buildNumber: '297',
                                            id: 'fdeaddb9-87af-446b-baff-91eb85d1446c',
                                            integrationId: 'apple-beta',
                                            isProcessed: true,
                                            processingState: 'VALID',
                                            type: 'ios-binary',
                                            uploadedAt: '2022-03-15T12:31:21-07:00',
                                        },
                                        associatedCIBuild: {
                                            branch: 'main',
                                            buildFinishedAt: '2022-03-15T19:28:41Z',
                                            buildHash: 'A5LZuDfhn3e6WNweXBzV8AR1w6c=',
                                            buildIdentifier: '297',
                                            buildStartedAt: '2022-03-15T19:17:48Z',
                                            buildStatus: 'success',
                                            buildUrl: 'https://app.bitrise.io/build/123',
                                            commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                            commitAuthor: null,
                                            commitMessage:
                                                'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main` for release 3.97.0.',
                                            integrationId: 'bitrise',
                                            providerBuildStatusString: 'Success',
                                            workflowRunId: '0a65a37e-ba7d-45d7-bd3f-3ec0ba117bd8',
                                        },
                                    },
                                    isConsideredExternal: true,
                                    isLatestBuild: false,
                                    isSelectedBuild: false,
                                    metrics: { numInstalls: 0 },
                                    selectedReason: '',
                                },
                                {
                                    betaGroups: [
                                        '6c1c4439-16cb-49e8-b7c4-97d7c1f46a51',
                                        '7874adb9-9af0-427f-aabd-f7d36bed9049',
                                    ],
                                    betaStatus: 'testing',
                                    betaTesters: [],
                                    betaTestingNotes: [
                                        {
                                            displayLocale: 'American English',
                                            id: 'c4fad929-20ef-4a3e-adc8-58425d2475d1',
                                            locale: 'en-US',
                                            notes: '',
                                        },
                                    ],
                                    betaBuildDetails: {
                                        autoNotifyEnabled: true,
                                        externalBuildState: 'IN_BETA_TESTING',
                                        internalBuildState: 'IN_BETA_TESTING',
                                    },
                                    appStoreBuild: {
                                        appStoreBuild: {
                                            buildNumber: '296',
                                            id: 'ac1fa4d1-8664-4679-ae19-9842afaff37e',
                                            integrationId: 'apple-beta',
                                            isProcessed: true,
                                            processingState: 'VALID',
                                            type: 'ios-binary',
                                            uploadedAt: '2022-03-15T11:50:03-07:00',
                                        },
                                        associatedCIBuild: {
                                            branch: 'main',
                                            buildFinishedAt: '2022-03-15T18:46:11Z',
                                            buildHash: '0qVMy0jBTbwzL5k67ZzU092K-wA=',
                                            buildIdentifier: '296',
                                            buildStartedAt: '2022-03-15T18:35:29Z',
                                            buildStatus: 'success',
                                            buildUrl: 'https://app.bitrise.io/build/123',
                                            commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                            commitAuthor: null,
                                            commitMessage:
                                                'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main` for release 3.97.0.',
                                            integrationId: 'bitrise',
                                            providerBuildStatusString: 'Success',
                                            workflowRunId: '482375f7-5c67-46a0-b3f3-0e24907c2e3c',
                                        },
                                        stabilityInfos: {},
                                    },
                                    isConsideredExternal: true,
                                    isLatestBuild: false,
                                    isSelectedBuild: false,
                                    metrics: { numInstalls: 0 },
                                    selectedReason: '',
                                },
                                {
                                    betaGroups: [
                                        '6c1c4439-16cb-49e8-b7c4-97d7c1f46a51',
                                        '7874adb9-9af0-427f-aabd-f7d36bed9049',
                                    ],
                                    betaStatus: 'testing',
                                    betaTesters: [],
                                    betaTestingNotes: [
                                        {
                                            displayLocale: 'American English',
                                            id: 'ec4ab077-1e79-4be5-bcfc-7f67fd846b92',
                                            locale: 'en-US',
                                            notes: '',
                                        },
                                    ],
                                    buildId: 'bb1a73c7-7645-499d-852a-7a6a96c7de30',
                                    appStoreBuild: {
                                        appStoreBuild: {
                                            buildNumber: '290',
                                            id: 'bb1a73c7-7645-499d-852a-7a6a96c7de30',
                                            integrationId: 'apple-beta',
                                            isProcessed: true,
                                            processingState: 'VALID',
                                            type: 'ios-binary',
                                            uploadedAt: '2022-03-11T12:08:29-08:00',
                                        },
                                        associatedCIBuild: {
                                            branch: 'main',
                                            buildFinishedAt: '2022-03-11T20:05:46Z',
                                            buildHash: 'cYAkkfz3H8wQ_0fBrtp05sJ6uMA=',
                                            buildIdentifier: '290',
                                            buildStartedAt: '2022-03-11T19:55:17Z',
                                            buildStatus: 'success',
                                            buildUrl: 'https://app.bitrise.io/build/123',
                                            commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                            commitAuthor: null,
                                            commitMessage:
                                                'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main` for release 3.97.0.',
                                            integrationId: 'bitrise',
                                            providerBuildStatusString: 'Success',
                                            workflowRunId: '66dee2d1-be63-43d5-a8b8-24bb8f19efbc',
                                        },
                                        stabilityInfos: {},
                                    },
                                    betaBuildDetails: {
                                        autoNotifyEnabled: true,
                                        externalBuildState: 'IN_BETA_TESTING',
                                        internalBuildState: 'IN_BETA_TESTING',
                                    },
                                    isConsideredExternal: true,
                                    isLatestBuild: false,
                                    isSelectedBuild: false,
                                    metrics: { numInstalls: 0 },
                                    selectedReason: '',
                                },
                            ],
                            betaGroups: [
                                {
                                    id: '6c1c4439-16cb-49e8-b7c4-97d7c1f46a51',
                                    isExternalGroup: false,
                                    name: 'App Store Connect Users',
                                    testers: ['info@demo.com'],
                                },
                                {
                                    id: 'ddbcb2dd-a9b3-4763-ba95-9db9f6325c80',
                                    isExternalGroup: true,
                                    name: 'ITC.apps.tf.labels.CustomGroup1',
                                    testers: [
                                        'aearhart@demo.com',
                                        'sully@demo.com',
                                        'buzz@demo.com',
                                        'tester1@gmail.com',
                                        'tester2@gmail.com',
                                        'tester3@gmail.com',
                                    ],
                                },
                                {
                                    id: '06c1a5c1-72ee-4ed1-83fd-151229eb9543',
                                    isExternalGroup: true,
                                    name: 'Another external testing group',
                                    testers: null,
                                },
                                {
                                    id: '7874adb9-9af0-427f-aabd-f7d36bed9049',
                                    isExternalGroup: true,
                                    name: 'The Best People',
                                    testers: ['aearhart@demo.com', 'sully@demo.com', 'buzz@demo.com'],
                                },
                            ],
                            buildInfo: {
                                associatedAppStoreBuild: {
                                    betaBuildDetails: {
                                        autoNotifyEnabled: true,
                                        externalBuildState: 'IN_BETA_TESTING',
                                        internalBuildState: 'IN_BETA_TESTING',
                                    },
                                    buildNumber: '297',
                                    id: 'fdeaddb9-87af-446b-baff-91eb85d1446c',
                                    integrationId: 'testflightx',
                                    isProcessed: true,
                                    processingState: 'VALID',
                                    type: 'ios-binary',
                                    uploadedAt: '2022-03-15T12:31:21-07:00',
                                },
                                ciBuild: {
                                    artifacts: [
                                        {
                                            downloadUrl: 'https://demo.runway.team',
                                            fileName: 'app.dSYM.zip',
                                            size: 16,
                                        },
                                        {
                                            downloadUrl: 'https://demo.runway.team',
                                            fileName: 'app.ipa',
                                            size: 139,
                                        },
                                    ],
                                    branch: 'main',
                                    buildFinishedAt: '2022-03-15T19:28:41Z',
                                    buildHash: 'A5LZuDfhn3e6WNweXBzV8AR1w6c=',
                                    buildIdentifier: '297',
                                    buildStartedAt: '2022-03-15T19:17:48Z',
                                    buildStatus: 'success',
                                    buildUrl: 'https://app.bitrise.io/build/123',
                                    commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                    commitAuthor: 'runway-github[bot]',
                                    commitMessage:
                                        'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main` for release 3.97.0.',
                                    integrationId: 'bitrise',
                                    providerBuildStatusString: 'Success',
                                    workflowRunId: '0a65a37e-ba7d-45d7-bd3f-3ec0ba117bd8',
                                },
                                isSelectedBuild: false,
                                selectedReason: 'autoRunwaySelection',
                            },
                            soakDidFinishEarly: true,
                            soakFinishedAt: '2022-03-04T18:05:50Z',
                            soakStartedAt: '2022-03-16T01:16:18.832845812Z',
                            testers: [
                                {
                                    email: 'tester1@gmail.com',
                                    firstName: 'Tester',
                                    id: '1',
                                    lastName: 'One',
                                },
                                {
                                    email: 'tester2@gmail.com',
                                    firstName: 'Tester',
                                    id: '2',
                                    lastName: 'Two',
                                },
                                {
                                    email: 'tester3@gmail.com',
                                    firstName: 'Tester',
                                    id: '3',
                                    lastName: 'Three',
                                },
                                {
                                    email: 'info@demo.com',
                                    firstName: 'Buzz',
                                    id: '4',
                                    lastName: 'Aldrin',
                                },
                                {
                                    email: 'tester5@gmail.com',
                                    firstName: 'Tester',
                                    id: '5',
                                    lastName: 'Five',
                                },
                                {
                                    email: 'tester6@gmail.com',
                                    firstName: 'Tester',
                                    id: '6',
                                    lastName: 'Six',
                                },
                                {
                                    email: 'tester7@gmail.com',
                                    firstName: 'Tester',
                                    id: '7',
                                    lastName: 'Seven',
                                },
                            ],
                        },
                        groupIndex: 1,
                        orderIndex: 2,
                        checklistItems: [],
                        automationRuns: iosStepAutomations['betaTesting'],
                    },
                    {
                        id: '1.0.0#screenshots',
                        type: 'screenshots',
                        description: 'Screenshots',
                        status: 'pending',
                        statusReasonString: 'Approval required.',
                        blockingConfig: 'Blocking',
                        data: {
                            isApproved: false,
                            requiresApproval: true,
                            screenshotLocalizations: [
                                {
                                    displayLocale: 'American English',
                                    locale: 'en-US',
                                    screenshotSets: [
                                        {
                                            screenshotDisplayType: 'APP_IPHONE_55',
                                            screenshots: [
                                                {
                                                    height: 2208,
                                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-iOS-1.png',
                                                    width: 1242,
                                                },
                                                {
                                                    height: 2208,
                                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-iOS-2.png',
                                                    width: 1242,
                                                },
                                            ],
                                        },
                                        {
                                            screenshotDisplayType: 'APP_IPHONE_65',
                                            screenshots: [
                                                {
                                                    height: 2688,
                                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-iOS-1.png',
                                                    width: 1242,
                                                },
                                                {
                                                    height: 2688,
                                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-iOS-2.png',
                                                    width: 1242,
                                                },
                                            ],
                                        },
                                    ],
                                },
                                {
                                    displayLocale: 'European Spanish',
                                    locale: 'es-ES',
                                    screenshotSets: [
                                        {
                                            screenshotDisplayType: 'APP_IPHONE_65',
                                            screenshots: [
                                                {
                                                    height: 2688,
                                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-iOS-1.png',
                                                    width: 1242,
                                                },
                                                {
                                                    height: 2688,
                                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-iOS-2.png',
                                                    width: 1242,
                                                },
                                            ],
                                        },
                                    ],
                                },
                            ],
                            storeUrl: 'https://appstoreconnect.apple.com',
                        },
                        groupIndex: 2,
                        orderIndex: 0,
                        checklistItems: [
                            {
                                id: 'c03218kcu89v1j3i28cg',
                                title: 'Upload screenshots to ASC per the guidelines',
                                description:
                                    'Here are the [guidelines](https://help.apple.com/app-store-connect/#/dev910472ff2).',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'screenshots',
                                approverGroups: ['design'],
                                releaseTypes: 'all',
                                notifyEnabled: false,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: iosStepAutomations['screenshots'],
                    },
                    {
                        id: '1.0.0#metadata',
                        type: 'metadata',
                        description: 'Metadata',
                        status: 'pending',
                        statusReasonString: 'Waiting for manual approval',
                        blockingConfig: 'Blocking',
                        data: {
                            isApproved: false,
                            localizations: [
                                {
                                    description:
                                        'This is a fun demo app to show you how cool releasing can be using Runway!',
                                    displayLocale: 'American English',
                                    id: '3.97.0:en-US',
                                    keywords: 'mobile,releases',
                                    locale: 'en-US',
                                    promotionalText: null,
                                    status: 'ready',
                                    whatsNew:
                                        'Minor bugfixes, some cool new features, and some foundational changes to keep things running smoothly!!',
                                },
                                {
                                    description:
                                        'Un demo divertido para enseñarles que facil es desarrollar applicaciones usando Runway!',
                                    displayLocale: 'European Spanish',
                                    id: '3.97.0:es-ES',
                                    keywords: 'mobile,releases',
                                    locale: 'es-ES',
                                    promotionalText: null,
                                    status: 'ready',
                                    whatsNew: 'Correcciónes de errores y mejoras menores.',
                                },
                            ],
                            requiresApproval: true,
                            storeUrl: 'https://appstoreconnect.apple.com',
                        },
                        groupIndex: 2,
                        orderIndex: 1,
                        checklistItems: [],
                        automationRuns: iosStepAutomations['metadata'],
                    },
                    {
                        id: '1.0.0#approvals',
                        type: 'approvals',
                        description: 'Approvals',
                        status: 'pending',
                        statusReasonString: 'There are 13 items that still require approval.',
                        blockingConfig: 'Blocking',
                        data: {
                            approvalItems: [
                                {
                                    approverGroups: ['qa'],
                                    comment: null,
                                    description: null,
                                    id: 'c51sfnlachdb4vli3720',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: {
                                        status: 'approved',
                                        createdAt: '2022-02-27T23:05:20.886428625Z',
                                        createdBy: 'ssullenberger@demo.com',
                                    },
                                    statuses: ['inProgress', 'blocked', 'rejected', 'approved'],
                                    title: 'Mobile - Checkout',
                                    type: 'multi',
                                    owners: ['ssullenberger@demo.com'],
                                },
                                {
                                    approverGroups: [],
                                    comment: {
                                        comment: 'Verified on latest, no issues',
                                        createdBy: 'aearhart@demo.com',
                                        createdAt: '2022-02-27T23:00:20.886428625Z',
                                    },
                                    description: null,
                                    id: 'c51p2qdachdas7jctn8g',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: {
                                        status: 'approved',
                                        createdAt: '2022-02-27T23:05:20.886428625Z',
                                        createdBy: 'aearhart@demo.com',
                                    },
                                    statuses: ['inProgress', 'blocked', 'rejected', 'approved'],
                                    title: 'Security/Ops',
                                    type: 'multi',
                                    owners: [],
                                },
                                {
                                    approverGroups: ['engineer'],
                                    comment: null,
                                    description: null,
                                    id: 'c51p10dachdas7jctn6g',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: null,
                                    statuses: ['inProgress', 'blocked', 'rejected', 'approved'],
                                    title: 'Mobile - Core',
                                    type: 'multi',
                                    owners: ['narmstrong@demo.com', 'ssullenberger@demo.com'],
                                },
                                {
                                    approverGroups: ['marketing'],
                                    comment: null,
                                    description: null,
                                    id: 'c55mpltachdaceuvmaug',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: null,
                                    statuses: ['inProgress', 'blocked', 'rejected', 'approved'],
                                    title: 'Sign-off from marketing',
                                    type: 'multi',
                                    owners: [],
                                },
                                {
                                    approverGroups: ['pilot'],
                                    comment: null,
                                    description: null,
                                    id: 'c51sif8m221m3fc0c4hg',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: null,
                                    statuses: ['inProgress', 'blocked', 'rejected', 'approved'],
                                    title: 'Get signoff from security/infra',
                                    type: 'multi',
                                    owners: [],
                                },
                                {
                                    approverGroups: ['pilot'],
                                    comment: null,
                                    description: null,
                                    id: 'c51sif8m221m3fc0c4i0',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: null,
                                    statuses: ['inProgress', 'blocked', 'rejected', 'approved'],
                                    title: 'Get signoff from compliance',
                                    type: 'multi',
                                    owners: [],
                                },
                            ],
                        },
                        groupIndex: 3,
                        orderIndex: 0,
                        checklistItems: [],
                        automationRuns: iosStepAutomations['approvals'],
                    },
                    {
                        id: '1.0.0#submission',
                        type: 'submission',
                        description: 'Submission',
                        status: 'pending',
                        statusReasonString: '3 checklist items still need to be completed for this step.',
                        blockingConfig: 'Blocking',
                        data: {
                            appStoreData: {
                                allInfoPresentForSubmission: true,
                                earliestReleaseDate: null,
                                exportComplianceInfoUsesEncryption: false,
                                integrationId: 'apple',
                                phasedRelease: {
                                    dayNumber: 0,
                                    isManuallyAcceleratedPhasedRollout: false,
                                    isRunwayAcceleratedRollout: false,
                                    isRunwayPaused: false,
                                    startDate: null,
                                    state: 'INACTIVE',
                                    userFraction: 0,
                                },
                                releaseRequested: false,
                                releaseType: 'MANUAL',
                                reviewDetail: {
                                    contactEmail: 'aearhart@demo.com',
                                    contactFirstName: 'Amelia',
                                    contactLastName: 'Earhart',
                                    contactPhone: '+(123)456-7891',
                                    demoAccountName: null,
                                    demoAccountPassword: null,
                                    demoAccountRequired: false,
                                    notes: '',
                                },
                                state: 'PREPARE_FOR_SUBMISSION',
                            },
                            storeUrl: 'https://appstoreconnect.apple.com',
                            submittedAt: null,
                            workflowData: null,
                        },
                        groupIndex: 4,
                        orderIndex: 0,
                        checklistItems: [
                            {
                                id: 'c5n2h7ln86bo9gnibnr0',
                                title: 'Thing to do before submit',
                                description: 'Do this!',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'submission',
                                approverGroups: ['approver'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'c8al5bdn86bs44bjrpd0',
                                title: 'Another thing to remember before submitting',
                                description: 'All this stuff',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'submission',
                                approverGroups: ['pilot', 'pm'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'c8e0r55n86boufv00ong',
                                title: 'Important Pre-submit step',
                                description: 'Remember to do this before submitting.',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'submission',
                                approverGroups: ['pilot', 'pm'],
                                releaseTypes: 'all',
                                notifyEnabled: false,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: iosStepAutomations['submission'],
                    },
                    {
                        id: '1.0.0#storeReview',
                        type: 'storeReview',
                        description: 'App store review',
                        status: 'inactive',
                        statusReasonString: 'Waiting for submission.',
                        blockingConfig: 'Blocking',
                        data: {
                            appStoreData: {
                                allInfoPresentForSubmission: true,
                                earliestReleaseDate: null,
                                exportComplianceInfoUsesEncryption: false,
                                integrationId: 'apple',
                                phasedRelease: {
                                    dayNumber: 0,
                                    isManuallyAcceleratedPhasedRollout: false,
                                    isRunwayAcceleratedRollout: false,
                                    isRunwayPaused: false,
                                    startDate: null,
                                    state: 'INACTIVE',
                                    userFraction: 0,
                                },
                                releaseRequested: false,
                                releaseType: 'MANUAL',
                                reviewDetail: {
                                    contactEmail: 'buzz@demo.com',
                                    contactFirstName: 'Buzz',
                                    contactLastName: 'Aldrin',
                                    contactPhone: '+1(123)456-7891',
                                    demoAccountName: null,
                                    demoAccountPassword: null,
                                    demoAccountRequired: false,
                                    notes: '',
                                },
                                state: 'PREPARE_FOR_SUBMISSION',
                            },
                            approvedAt: null,
                            devRejectedAt: null,
                            startedReviewAt: null,
                            storeRejectedAt: null,
                            storeUrl: 'https://appstoreconnect.apple.com',
                            submittedAt: null,
                        },
                        groupIndex: 4,
                        orderIndex: 1,
                        checklistItems: [
                            {
                                id: 'c7oomvdn86bpp9lejr1g',
                                title: 'Release via managed publishing if needed',
                                description: null,
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'storeReview',
                                approverGroups: ['pilot'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'c59kpldachdcretdibcg',
                                title: 'Many different users can deal with this',
                                description: 'engineers, qa and pms should be able to approve this',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'storeReview',
                                approverGroups: ['engineer', 'qa', 'pm'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: iosStepAutomations['storeReview'],
                    },
                    {
                        id: '1.0.0#takeoff',
                        type: 'takeoff',
                        description: 'Release',
                        status: 'inactive',
                        statusReasonString: 'Waiting for App Store review to be completed...',
                        blockingConfig: 'Blocking',
                        data: {
                            appStoreData: {
                                allInfoPresentForSubmission: true,
                                earliestReleaseDate: null,
                                exportComplianceInfoUsesEncryption: false,
                                integrationId: 'apple',
                                phasedRelease: {
                                    dayNumber: 0,
                                    isManuallyAcceleratedPhasedRollout: false,
                                    isRunwayAcceleratedRollout: false,
                                    isRunwayPaused: false,
                                    startDate: null,
                                    state: 'INACTIVE',
                                    userFraction: 0,
                                },
                                releaseRequested: false,
                                releaseType: 'MANUAL',
                                reviewDetail: {
                                    contactEmail: 'buzz@demo.com',
                                    contactFirstName: 'Buzz',
                                    contactLastName: 'Aldrin',
                                    contactPhone: '+16507395445',
                                    demoAccountName: null,
                                    demoAccountPassword: null,
                                    demoAccountRequired: false,
                                    notes: 'Use a clean, itemized restaurant receipt to test.\nThere is no password for sign-in: enter an email address, and a secure sign-in link will be emailed.',
                                },
                                state: 'WAITING_FOR_REVIEW',
                            },
                            appStoreUrl: 'https://apps.apple.com',
                            storeUrl: 'https://appstoreconnect.apple.com',
                            tagData: {
                                codeDiffUrl: 'https://github.com/demo/demo-ios/compare',
                                expectedReleaseTagName: 'v3.97.0',
                                releaseTag: null,
                                releaseTagState: 'notTaggable',
                                taggableCommits: [],
                            },
                        },
                        groupIndex: 4,
                        orderIndex: 2,
                        checklistItems: [
                            {
                                id: 'bvv18nkfoo7cohhp7ju0',
                                title: 'Send announcement email to team@ distribution list',
                                description: null,
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'takeoff',
                                approverGroups: ['pilot'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: iosStepAutomations['takeoff'],
                    },
                ],
        stats:
            platform === AppPlatform.Android ?
                [
                    {
                        value: 61,
                        type: 'LinesOfCodeChanged',
                        average: 7371,
                        change: -7310,
                        changeStatus: 'Neutral',
                        unit: 'count',
                        additionalData: null,
                        category: 'work',
                    },
                    {
                        value: 100,
                        type: 'CIWorkflowSuccessPercent',
                        average: 100,
                        change: 0,
                        changeStatus: 'Neutral',
                        unit: 'percent',
                        additionalData: null,
                        category: 'build',
                    },
                    {
                        value: 164972,
                        type: 'ReleaseDuration',
                        average: 9404635,
                        change: -9239663,
                        changeStatus: 'Neutral',
                        unit: 'seconds',
                        additionalData: null,
                        category: 'work',
                    },
                ]
            :   [
                    {
                        value: 164972,
                        type: 'ReleaseDuration',
                        average: 9404635,
                        change: -9239663,
                        changeStatus: 'Neutral',
                        unit: 'seconds',
                        additionalData: null,
                        category: 'work',
                    },
                    {
                        value: 22273,
                        type: 'WaitingForReviewDuration',
                        average: 54431,
                        change: -32158,
                        changeStatus: 'Good',
                        unit: 'seconds',
                        additionalData: null,
                        category: 'work',
                    },
                    {
                        value: 1667,
                        type: 'InReviewDuration',
                        average: 3347,
                        change: -1680,
                        changeStatus: 'Good',
                        unit: 'seconds',
                        additionalData: null,
                        category: 'work',
                    },
                ],
        releaseSummaryMessage: 'We have successfully released this version! 🎉',
        releasePilotId: 'aearhart@demo.com',
        releaseDuration: 26000,
        appStoreReviewData: {
            averageRating: 4.51706,
            totalRatingsCount: 586,
            issues: [],
            recentReviews: [
                {
                    author: '',
                    version: '1.0.0',
                    rating: '4',
                    id: '8542838247',
                    title: 'Love the app!',
                    content:
                        "I love the app. It was easy to use. This is one of my favorite apps and I can't wait to see what they add to it next!.",
                    fetchedAt: '2022-04-09T02:56:53.521102766Z',
                    submittedAt: '2022-04-07T14:51:08-07:00',
                },
                {
                    author: '',
                    version: '1.0.0',
                    rating: '5',
                    id: '8528852509',
                    title: 'Super helpful app',
                    content: 'Amazing bc I never have to worry about releases going poorly again!',
                    fetchedAt: '2022-04-05T11:38:50.77282+01:00',
                    submittedAt: '2022-04-03T16:54:44-07:00',
                },
                {
                    author: '',
                    version: '1.0.0',
                    rating: '3',
                    id: '8524205862',
                    title: 'This is one of my favorite apps',
                    content: 'I use it all the time and I tell all my friends about it. A+',
                    fetchedAt: '2022-04-05T11:38:50.77282+01:00',
                    submittedAt: '2022-04-02T11:17:22-07:00',
                },
            ],
            viewAllReviewsUrl: 'https://apps.apple.com/us/app/123456/reviews',
        },
        appStoreState: AppleAppStoreState.ReadyForSale,
        phasedRelease: null,
        appStoreBuilds: [
            {
                isRecentBuild: false,
                appStoreBuild: {
                    buildNumber: '283',
                    id: '7d20bf47-8497-4f2e-a6e5-fffd00bdc468',
                    integrationId: 'apple',
                    isProcessed: true,
                    processingState: 'VALID',
                    type: 'ios-binary',
                    uploadedAt: '2022-03-01T14:16:56-08:00',
                },
                associatedCIBuild: {
                    buildHash: 'abcdefg',
                    commitUrl: '',
                    buildIdentifier: '283',
                    integrationId: 'bitrise',
                    buildUrl: 'https://app.bitrise.io/build/abcdefg',
                    buildStartedAt: '2022-03-01T22:03:51Z',
                    buildFinishedAt: '2022-03-01T22:14:17Z',
                    buildStatus: 'success',
                    providerBuildStatusString: 'Success',
                    commit: 'eb3ff48712021d32ec6f395452d19a2efeeae1d2',
                    commitMessage:
                        'Merge pull request #116 from demo/development\n\nMerge development -\u003e main for release',
                    commitAuthor: null,
                    workflowRunId: '123-abc',
                    branch: '',
                },
                isCurrentlySelected: false,
                isNewestAppStoreBuild: false,
                stabilityInfos: {
                    'sentry:xid': {
                        version: '1.0.0',
                        integrationId: 'sentry',
                        sessionStability: 99.95987785034438,
                        userStability: 99.95534195304525,
                        sessionAdoption: 5.29375639713409,
                        userAdoption: 5.80524642289348,
                        adoptionPeriodString: '24-48H',
                        url: 'https://sentry.com',
                        topStabilityIssues: topStabilityIssues(platform),
                        topNewStabilityIssues: topNewStabilityIssues(platform),
                        topStabilityIssuesNonFatal: [],
                        topNewStabilityIssuesNonFatal: [],
                        isProdEligible: false,
                        isBetaEligible: false,
                    },
                },
                selectedBuildReason: null,
            },
        ],
    },
    nextRelease: {
        type: 'minor',
        events: [],
        lifecycleAutomations: [],
        displayableVersion: '1.1.0',
        releaseName: null,
        id: 'demo:1.1.0',
        releaseId: 'demo:1.1.0',
        version: '1.1.0',
        planningSummary: 'First version release!',
        targetKickoffDate: '2022-03-03T18:00:00Z',
        targetSubmitDate: '2022-03-23T17:00:00Z',
        targetReleaseDate: '2022-03-25T17:00:00Z',
        releasePilotId: 'aearhart@demo.com',
        percentFeatureComplete: 40,
        isKickedOff: true,
        isSubmitted: false,
        featuresStep: getCompactDataFromFeaturesStep(featuresStep(platform)),
        stats: [
            {
                value: 61,
                type: 'LinesOfCodeChanged',
                average: 7371,
                change: -7310,
                changeStatus: 'Neutral',
                unit: 'count',
                additionalData: null,
                category: 'work',
            },
            {
                value: 100,
                type: 'CIWorkflowSuccessPercent',
                average: 100,
                change: 0,
                changeStatus: 'Neutral',
                unit: 'percent',
                additionalData: null,
                category: 'build',
            },
            {
                value: 0,
                type: 'ItemsCompleted',
                average: 59,
                change: -59,
                changeStatus: 'Neutral',
                unit: 'count',
                additionalData: null,
                category: 'work',
            },
        ],
        steps:
            platform === AppPlatform.Android ?
                [
                    {
                        id: '1.0.0#kickoff',
                        type: 'kickoff',
                        description: 'Kickoff',
                        status: 'ready',
                        statusReasonString: 'Kicked off!',
                        blockingConfig: 'Blocking',
                        data: {
                            appUsesStaticReleaseBranch: false,
                            releaseBranchCodeVersionData: {
                                branchName: 'main',
                                bumpVersionPullRequest: null,
                                bumpVersionStatus: 'detected',
                                canPerformBumpVersion: false,
                                currentCodeVersionExternalString: '1.0.0',
                                targetCodeVersionExternalString: '1.0.0',
                                targetCodeVersionInternalString: '1.0.0',
                            },
                            workingBranchCodeVersionData: {
                                branchName: 'master',
                                bumpVersionPullRequest: {
                                    id: 'PR_123',
                                    number: 96,
                                    pullRequestType: 'BumpVersion',
                                    status: 'merged',
                                    title: 'Bumping version',
                                    url: 'https://github.com/runway-org/fake-app/pull/96',
                                },
                                bumpVersionStatus: 'merged',
                                canPerformBumpVersion: false,
                                currentCodeVersionExternalString: '1.0.0',
                                targetCodeVersionExternalString: '1.0.0',
                                targetCodeVersionInternalString: '1.0.0',
                            },
                            canPerformCodePromotion: false,
                            codePromotionPullRequest: null,
                            codePromotionStatus: '',
                            doesReleaseBranchExist: true,
                            hasPerformedAnyCodePromotion: false,
                            hotfixBranchingBase: null,
                            previousReleaseTag: 'v1.24.0',
                            previousReleaseVersion: '1.24.0',
                            previousTaggedReleaseTag: 'v1.24.0',
                            previousTaggedReleaseTagVersionsBehind: 0,
                            releaseBranch: 'release-1.25.0',
                            releaseBranchDiffUrl: 'https://github.com',
                            releaseBranchUrl: 'https://github.com',
                            resolvedFeatureAffiliations: [
                                {
                                    affiliationType: 'jira-fix-version',
                                    integrationId: 'jira',
                                    resolvedString: '3.97.0',
                                },
                            ],
                            workingBranchDiffUrl: null,
                        },
                        groupIndex: 0,
                        orderIndex: 0,
                        checklistItems: [
                            {
                                id: 'abc',
                                title: 'Pre-kickoff reminders',
                                description: 'Description   \u003cb\u003eyo\u003c/b\u003e !!',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'kickoff',
                                approverGroups: ['pilot'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: {
                                    status: 'approved',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'def',
                                title: 'Send out reminder for upcoming release deadlines',
                                description: 'Some detail',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'kickoff',
                                approverGroups: ['pilot'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: {
                                    status: 'approved',
                                    createdAt: '2022-03-03T18:03:32.236911544Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                comment: {
                                    comment: 'Reminder sent via email',
                                    createdAt: '2022-03-03T18:00:32.236911544Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'jkl',
                                title: 'Verify versioning with product team',
                                description: null,
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'kickoff',
                                approverGroups: [],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: {
                                    status: 'approved',
                                    createdAt: '2022-03-03T18:03:33.133516479Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: androidStepAutomations['kickoff'],
                    },
                    {
                        id: '1.0.0#featureReadiness',
                        type: 'featureReadiness',
                        description: 'Feature readiness',
                        status: 'pending',
                        statusReasonString: 'Some work for this release is not yet complete.',
                        blockingConfig: 'Blocking',
                        data: { items: null, percentComplete: 0, workingBranchPercentComplete: null },
                        groupIndex: 0,
                        orderIndex: 1,
                        checklistItems: [
                            {
                                id: 'abc',
                                title: 'Verify production feature flags are correct for this release',
                                description: null,
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'featureReadiness',
                                approverGroups: ['engineer'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: {
                                    status: 'approved',
                                    createdAt: '2022-03-15T22:54:38.462070296Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'def',
                                title: 'Update CMS version flags for this release',
                                description: '',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'featureReadiness',
                                approverGroups: [],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: {
                                    status: 'approved',
                                    createdAt: '2022-03-15T22:54:39.472656772Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: androidStepAutomations['featureReadiness'],
                    },
                    {
                        id: '1.0.0#releaseCandidate',
                        type: 'releaseCandidate',
                        description: 'CI',
                        status: 'ready',
                        statusReasonString: 'CI build is ready!',
                        blockingConfig: 'Blocking',
                        data: {
                            builds: [
                                {
                                    associatedAppStoreBuild: {
                                        betaBuildDetails: { status: 'completed', userFraction: 0 },
                                        buildNumber: '27500',
                                        id: '27500',
                                        integrationId: 'google-beta',
                                        isProcessed: true,
                                        status: 'completed',
                                        type: 'android-aab',
                                        uploadedAt: '2022-03-15T18:11:14.055381Z',
                                        userFraction: 0,
                                    },
                                    ciBuild: {
                                        artifacts: [],
                                        branch: 'release-1.25.0',
                                        buildFinishedAt: '2022-03-15T18:09:36Z',
                                        buildHash: '5dGgafjgm3ECgAvSmUmyok8bFZY=',
                                        buildIdentifier: '275',
                                        buildStartedAt: '2022-03-15T18:06:21Z',
                                        buildStatus: 'success',
                                        buildUrl: 'https://app.bitrise.io/build',
                                        commit: '8377f75e11ef0544ac5d7b138f8ec1af913203be',
                                        commitAuthor: 'runwayhelperdev[bot]',
                                        commitMessage:
                                            'Bumping version (#41)\n\nCo-authored-by: runwayhelperdev[bot] \u003c68658124+runwayhelperdev[bot]@users.noreply.github.com\u003e',
                                        integrationId: 'bitrise',
                                        providerBuildStatusString: 'Success',
                                        workflowRunId: '1234',
                                    },
                                    isSelectedBuild: true,
                                    selectedReason: 'autoRunwaySelection',
                                },
                                {
                                    associatedAppStoreBuild: {
                                        betaBuildDetails: { status: null, userFraction: 0 },
                                        buildNumber: '27100',
                                        id: '27100',
                                        integrationId: 'google-beta',
                                        isProcessed: true,
                                        status: null,
                                        type: 'android-aab',
                                        uploadedAt: '2022-03-15T15:53:26.217791Z',
                                        userFraction: 0,
                                    },
                                    ciBuild: {
                                        artifacts: [],
                                        branch: 'release-1.25.0',
                                        buildFinishedAt: '2022-03-15T15:53:31Z',
                                        buildHash: 'abc',
                                        buildIdentifier: '271',
                                        buildStartedAt: '2022-03-15T15:50:06Z',
                                        buildStatus: 'success',
                                        buildUrl: 'https://app.bitrise.io/build',
                                        commit: '8377f75e11ef0544ac5d7b138f8ec1af913203be',
                                        commitAuthor: 'runwayhelperdev[bot]',
                                        commitMessage:
                                            'Bumping version (#41)\n\nCo-authored-by: runwayhelperdev[bot] \u003c68658124+runwayhelperdev[bot]@users.noreply.github.com\u003e',
                                        integrationId: 'bitrise',
                                        providerBuildStatusString: 'Success',
                                        workflowRunId: '1234',
                                    },
                                    isSelectedBuild: false,
                                    selectedReason: 'autoRunwaySelection',
                                },
                                {
                                    associatedAppStoreBuild: {
                                        betaBuildDetails: { status: null, userFraction: 0 },
                                        buildNumber: '26600',
                                        id: '26600',
                                        integrationId: 'google-beta',
                                        isProcessed: true,
                                        status: null,
                                        type: 'android-aab',
                                        uploadedAt: '2022-02-22T15:10:44.431772-06:00',
                                        userFraction: 0,
                                    },
                                    ciBuild: {
                                        artifacts: [],
                                        branch: 'release-1.25.0',
                                        buildFinishedAt: '2022-02-22T21:09:21Z',
                                        buildHash: 'xIF7XbFDz_ILtKZjP3TOnVythwo=',
                                        buildIdentifier: '266',
                                        buildStartedAt: '2022-02-22T21:05:24Z',
                                        buildStatus: 'success',
                                        buildUrl: 'https://app.bitrise.io/build/339716b3-ad88-417a-a16c-6b291ed196db',
                                        commit: '8377f75e11ef0544ac5d7b138f8ec1af913203be',
                                        commitAuthor: 'runwayhelperdev[bot]',
                                        commitMessage:
                                            'Bumping version to 1.25.0 (#41)\n\nCo-authored-by: runwayhelperdev[bot] \u003c68658124+runwayhelperdev[bot]@users.noreply.github.com\u003e',
                                        integrationId: 'bitrise',
                                        providerBuildStatusString: 'Success',
                                        workflowRunId: '339716b3-ad88-417a-a16c-6b291ed196db',
                                    },
                                    isSelectedBuild: false,
                                    selectedReason: 'autoRunwaySelection',
                                },
                                {
                                    associatedAppStoreBuild: {
                                        betaBuildDetails: { status: null, userFraction: 0 },
                                        buildNumber: '26300',
                                        id: '26300',
                                        integrationId: 'google-beta',
                                        isProcessed: true,
                                        status: null,
                                        type: 'android-aab',
                                        uploadedAt: '2022-02-14T18:29:27.228176572Z',
                                        userFraction: 0,
                                    },
                                    ciBuild: {
                                        artifacts: [],
                                        branch: 'release-1.25.0',
                                        buildFinishedAt: '2022-02-14T18:29:57Z',
                                        buildHash: 'EoC1sXC38yCv2FysjX3xoT8joUE=',
                                        buildIdentifier: '263',
                                        buildStartedAt: '2022-02-14T18:26:48Z',
                                        buildStatus: 'success',
                                        buildUrl: 'https://app.bitrise.io/build/c8bc9aa2-864f-46a0-99c8-c38fff620763',
                                        commit: '2a9abf9fb4eb2355dfd1600b622ea4f63ee4405d',
                                        commitAuthor: null,
                                        commitMessage:
                                            'Bumping version to 1.24.0 (#40)\n\nCo-authored-by: runwayhelperdev[bot] \u003c68658124+runwayhelperdev[bot]@users.noreply.github.com\u003e',
                                        integrationId: 'bitrise',
                                        providerBuildStatusString: 'Success',
                                        workflowRunId: 'c8bc9aa2-864f-46a0-99c8-c38fff620763',
                                    },
                                    isSelectedBuild: false,
                                    selectedReason: 'autoRunwaySelection',
                                },
                            ],
                            workflowData: {
                                branch: 'release-1.25.0',
                                headCommit: '8377f75e11ef0544ac5d7b138f8ec1af913203be',
                                headCommitAuthor: 'runwayhelperdev[bot]',
                                headCommitMessage:
                                    'Bumping version to 1.25.0 (#41)\n\nCo-authored-by: runwayhelperdev[bot] \u003c68658124+runwayhelperdev[bot]@users.noreply.github.com\u003e',
                                integrationType: 'bitrise',
                                workflowName: 'release',
                                workflowType: 'staging',
                                workflowUrl:
                                    'https://app.bitrise.io/app/d09c63f8c17bb806/workflow_editor#!/workflows?workflow_id=release',
                            },
                        },
                        groupIndex: 1,
                        orderIndex: 0,
                        checklistItems: [],
                        automationRuns: androidStepAutomations['releaseCandidate'],
                    },
                    {
                        id: '1.0.0#regressionTesting',
                        type: 'regressionTesting',
                        description: 'Regression testing',
                        status: 'pending',
                        statusReasonString: 'Regression testing in progress...',
                        blockingConfig: 'Blocking',
                        data: {
                            availableBuilds: [
                                {
                                    appStoreBuildIdentifier: '27500',
                                    appStoreIntegrationId: 'google',
                                    buildIdentifier: '275',
                                    hash: '5dGgafjgm3ECgAvSmUmyok8bFZY=',
                                    integrationId: 'bitrise',
                                },
                                {
                                    appStoreBuildIdentifier: '27100',
                                    appStoreIntegrationId: 'google',
                                    buildIdentifier: '271',
                                    hash: 'v_LYY2Q_tHP__uFMuoahvSvrjbs=',
                                    integrationId: 'bitrise',
                                },
                                {
                                    appStoreBuildIdentifier: '26600',
                                    appStoreIntegrationId: 'google',
                                    buildIdentifier: '266',
                                    hash: 'xIF7XbFDz_ILtKZjP3TOnVythwo=',
                                    integrationId: 'bitrise',
                                },
                                {
                                    appStoreBuildIdentifier: '26300',
                                    appStoreIntegrationId: 'google',
                                    buildIdentifier: '263',
                                    hash: 'EoC1sXC38yCv2FysjX3xoT8joUE=',
                                    integrationId: 'bitrise',
                                },
                            ],
                            regressionBuildInfo: {
                                associatedAppStoreBuild: {
                                    betaBuildDetails: { status: 'completed', userFraction: 0 },
                                    buildNumber: '27500',
                                    id: '27500',
                                    integrationId: 'google-beta',
                                    isProcessed: true,
                                    status: 'completed',
                                    type: 'android-aab',
                                    uploadedAt: '2022-03-15T18:11:14.055381Z',
                                    userFraction: 0,
                                },
                                ciBuild: {
                                    artifacts: null,
                                    branch: 'release-1.25.0',
                                    buildFinishedAt: '2022-03-15T18:09:36Z',
                                    buildHash: '5dGgafjgm3ECgAvSmUmyok8bFZY=',
                                    buildIdentifier: '275',
                                    buildStartedAt: '2022-03-15T18:06:21Z',
                                    buildStatus: 'success',
                                    buildUrl: 'https://app.bitrise.io/build/bc829153-e305-4058-bf4c-9f9d04fae7a9',
                                    commit: '8377f75e11ef0544ac5d7b138f8ec1af913203be',
                                    commitAuthor: 'runwayhelperdev[bot]',
                                    commitMessage:
                                        'Bumping version to 1.25.0 (#41)\n\nCo-authored-by: runwayhelperdev[bot] \u003c68658124+runwayhelperdev[bot]@users.noreply.github.com\u003e',
                                    integrationId: 'bitrise',
                                    providerBuildStatusString: 'Success',
                                    workflowRunId: 'bc829153-e305-4058-bf4c-9f9d04fae7a9',
                                },
                            },
                            status: 'inProgress',
                            testItems: [
                                {
                                    approverGroups: ['pilot'],
                                    comment: null,
                                    description: 'And make sure to do it in an efficient way',
                                    id: 'c4tuob32dnc706n8d30g',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: null,
                                    statuses: ['inProgress', 'blocked', 'failed', 'passed'],
                                    title: 'Run full checkout flow',
                                    type: 'multi',
                                    owners: ['ssullenberger@demo.com'],
                                },
                            ],
                            nonHistoricalTestRunsCompletionFraction: 0.95,
                            nonHistoricalTestRunsStatusesTotalCount: 30,
                            testRuns: [
                                {
                                    completedAt: null,
                                    completedCount: 10,
                                    createdAt: '2022-02-17T23:48:12Z',
                                    description: '',
                                    id: '1',
                                    integrationId: 'testrail',
                                    isInProgress: true,
                                    name: 'Test run',
                                    statuses: [
                                        { color: '6EC78B', count: 10, displayableName: 'Passed', fraction: 1 },
                                        { color: '595959', count: 1, displayableName: 'Blocked', fraction: 0 },
                                        { color: 'F5F5F5', count: 5, displayableName: 'Untested', fraction: 0 },
                                        { color: 'F4AD22', count: 1, displayableName: 'Retest', fraction: 0 },
                                        { color: 'EF2D56', count: 1, displayableName: 'Failed', fraction: 0 },
                                    ],
                                    totalCount: 18,
                                    updatedAt: '2022-02-17T23:52:10Z',
                                    url: 'https://demo.testrail.io',
                                    completionFraction: 0.55,
                                },
                            ],
                            updatedAt: '2022-03-23T20:27:22.163903405Z',
                        },
                        groupIndex: 1,
                        orderIndex: 1,
                        checklistItems: [],
                        automationRuns: androidStepAutomations['regressionTesting'],
                    },
                    {
                        id: '1.0.0#betaTesting',
                        type: 'betaTesting',
                        description: 'Beta testing',
                        status: 'pending',
                        statusReasonString: 'Beta soak in progress. Waiting for manual approval.',
                        blockingConfig: 'Blocking',
                        data: {
                            betaBuilds: [
                                {
                                    betaGroups: [],
                                    betaStatus: 'testing',
                                    betaTesters: [],
                                    betaTestingNotes: [
                                        {
                                            displayLocale: 'American English',
                                            id: '1.25.0:en-US',
                                            locale: 'en-US',
                                            notes: 'Preparing to release redesign of product page, redesign of checkout.',
                                        },
                                        {
                                            displayLocale: 'Australian English',
                                            id: '1.25.0:en-AU',
                                            locale: 'en-AU',
                                            notes: 'Preparing to release redesign of product page, redesign of checkout.',
                                        },
                                        {
                                            displayLocale: 'Latin American Spanish',
                                            id: '1.25.0:es-419',
                                            locale: 'es-419',
                                            notes: 'Preparing to release redesign of product page, redesign of checkout.',
                                        },
                                        {
                                            displayLocale: 'European Spanish',
                                            id: '1.25.0:es-ES',
                                            locale: 'es-ES',
                                            notes: 'Preparing to release redesign of product page, redesign of checkout.',
                                        },
                                    ],
                                    buildId: '27500',
                                    appStoreBuild: {
                                        appStoreBuild: {
                                            buildNumber: '27500',
                                            id: '27500',
                                            integrationId: 'google-beta',
                                            isProcessed: true,
                                            status: 'completed',
                                            type: 'android-aab',
                                            uploadedAt: '2022-03-15T18:11:14.055381Z',
                                        },
                                        associatedCIBuild: {
                                            branch: 'release-1.25.0',
                                            buildFinishedAt: '2022-03-15T18:09:36Z',
                                            buildHash: '5dGgafjgm3ECgAvSmUmyok8bFZY=',
                                            buildIdentifier: '275',
                                            buildStartedAt: '2022-03-15T18:06:21Z',
                                            buildStatus: 'success',
                                            buildUrl: 'https://app.bitrise.io/build/123',
                                            commit: '8377f75e11ef0544ac5d7b138f8ec1af913203be',
                                            commitAuthor: null,
                                            commitMessage:
                                                'Bumping version (#41)\n\nCo-authored-by: runwayhelperdev[bot] \u003c68658124+runwayhelperdev[bot]@users.noreply.github.com\u003e',
                                            integrationId: 'bitrise',
                                            providerBuildStatusString: 'Success',
                                            workflowRunId: 'bc829153-e305-4058-bf4c-9f9d04fae7a9',
                                        },
                                        stabilityInfos: {},
                                    },
                                    betaBuildDetails: { status: 'completed', userFraction: 0 },
                                    isConsideredExternal: false,
                                    isLatestBuild: true,
                                    isSelectedBuild: true,
                                    metrics: { numInstalls: 0 },
                                    selectedReason: 'autoRunwaySelection',
                                },
                                {
                                    betaGroups: [],
                                    betaStatus: 'not-testing',
                                    betaTesters: [],
                                    betaTestingNotes: null,
                                    buildId: '27100',
                                    appStoreBuild: {
                                        associatedCIBuild: {
                                            branch: 'release-1.25.0',
                                            buildFinishedAt: '2022-03-15T15:53:31Z',
                                            buildHash: 'v_LYY2Q_tHP__uFMuoahvSvrjbs=',
                                            buildIdentifier: '271',
                                            buildStartedAt: '2022-03-15T15:50:06Z',
                                            buildStatus: 'success',
                                            buildUrl: 'https://app.bitrise.io',
                                            commit: '8377f75e11ef0544ac5d7b138f8ec1af913203be',
                                            commitAuthor: null,
                                            commitMessage:
                                                'Bumping version to 1.25.0 (#41)\n\nCo-authored-by: runwayhelperdev[bot] \u003c68658124+runwayhelperdev[bot]@users.noreply.github.com\u003e',
                                            integrationId: 'bitrise',
                                            providerBuildStatusString: 'Success',
                                            workflowRunId: '459e9bfc-b185-4acf-b95f-caa50d70da40',
                                        },
                                        appStoreBuild: {
                                            buildNumber: '27100',
                                            id: '27100',
                                            integrationId: 'google-beta',
                                            isProcessed: true,
                                            status: null,
                                            type: 'android-aab',
                                            uploadedAt: '2022-03-15T15:53:26.217791Z',
                                        },
                                        stabilityInfos: {},
                                    },
                                    betaBuildDetails: { status: null, userFraction: 0 },
                                    isConsideredExternal: false,
                                    isLatestBuild: false,
                                    isSelectedBuild: false,
                                    metrics: { numInstalls: 0 },
                                    selectedReason: '',
                                },
                                {
                                    betaGroups: [],
                                    betaStatus: 'not-testing',
                                    betaTesters: [],
                                    betaTestingNotes: null,
                                    buildId: '26600',
                                    appStoreBuild: {
                                        appStoreBuild: {
                                            buildNumber: '26600',
                                            id: '26600',
                                            integrationId: 'google-beta',
                                            isProcessed: true,
                                            status: null,
                                            type: 'android-aab',
                                            uploadedAt: '2022-02-22T15:10:44.431772-06:00',
                                        },
                                        associatedCIBuild: {
                                            branch: 'release-1.25.0',
                                            buildFinishedAt: '2022-02-22T21:09:21Z',
                                            buildHash: 'xIF7XbFDz_ILtKZjP3TOnVythwo=',
                                            buildIdentifier: '266',
                                            buildStartedAt: '2022-02-22T21:05:24Z',
                                            buildStatus: 'success',
                                            buildUrl: 'https://app.bitrise.io',
                                            commit: '8377f75e11ef0544ac5d7b138f8ec1af913203be',
                                            commitAuthor: null,
                                            commitMessage:
                                                'Bumping version to 1.25.0 (#41)\n\nCo-authored-by: runwayhelperdev[bot] \u003c68658124+runwayhelperdev[bot]@users.noreply.github.com\u003e',
                                            integrationId: 'bitrise',
                                            providerBuildStatusString: 'Success',
                                            workflowRunId: '339716b3-ad88-417a-a16c-6b291ed196db',
                                        },
                                        stabilityInfos: {},
                                    },
                                    betaBuildDetails: { status: null, userFraction: 0 },
                                    isConsideredExternal: false,
                                    isLatestBuild: false,
                                    isSelectedBuild: false,
                                    metrics: { numInstalls: 0 },
                                    selectedReason: '',
                                },
                                {
                                    betaGroups: [],
                                    betaStatus: 'not-testing',
                                    betaTesters: [],
                                    betaTestingNotes: null,
                                    buildId: '26300',
                                    appStoreBuild: {
                                        appStoreBuild: {
                                            buildNumber: '26300',
                                            id: '26300',
                                            integrationId: 'google-beta',
                                            isProcessed: true,
                                            status: null,
                                            type: 'android-aab',
                                            uploadedAt: '2022-02-14T18:29:27.228176572Z',
                                        },
                                        associatedCIBuild: {
                                            branch: 'release-1.25.0',
                                            buildFinishedAt: '2022-02-14T18:29:57Z',
                                            buildHash: 'EoC1sXC38yCv2FysjX3xoT8joUE=',
                                            buildIdentifier: '263',
                                            buildStartedAt: '2022-02-14T18:26:48Z',
                                            buildStatus: 'success',
                                            buildUrl: 'https://app.bitrise.io',
                                            commit: '2a9abf9fb4eb2355dfd1600b622ea4f63ee4405d',
                                            commitAuthor: null,
                                            commitMessage:
                                                'Bumping version to 1.24.0 (#40)\n\nCo-authored-by: runwayhelperdev[bot] \u003c68658124+runwayhelperdev[bot]@users.noreply.github.com\u003e',
                                            integrationId: 'bitrise',
                                            providerBuildStatusString: 'Success',
                                            workflowRunId: 'c8bc9aa2-864f-46a0-99c8-c38fff620763',
                                        },
                                        stabilityInfos: {},
                                    },
                                    betaBuildDetails: { status: null, userFraction: 0 },
                                    isConsideredExternal: false,
                                    isLatestBuild: false,
                                    isSelectedBuild: false,
                                    metrics: { numInstalls: 0 },
                                    selectedReason: '',
                                },
                            ],
                            betaGroups: [
                                {
                                    id: 'windock-testing-group@googlegroups.com',
                                    isExternalGroup: true,
                                    name: 'windock-testing-group@googlegroups.com',
                                    testers: [],
                                },
                            ],
                            soakDidFinishEarly: false,
                            soakFinishedAt: null,
                            soakStartedAt: '2022-03-15T18:11:14.055381Z',
                            testers: null,
                        },
                        groupIndex: 1,
                        orderIndex: 2,
                        checklistItems: [],
                        automationRuns: androidStepAutomations['betaTesting'],
                    },
                    {
                        id: '1.0.0#screenshots',
                        type: 'screenshots',
                        description: 'Screenshots',
                        status: 'pending',
                        statusReasonString: 'Approval required.',
                        blockingConfig: 'Blocking',
                        data: {
                            isApproved: false,
                            requiresApproval: true,
                            screenshotLocalizations: [
                                {
                                    displayLocale: 'American English',
                                    locale: 'en-US',
                                    screenshotSets: [
                                        {
                                            screenshotDisplayType: 'phoneScreenshots',
                                            screenshots: [
                                                {
                                                    height: 1562,
                                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-Android-1.png',
                                                    width: 2778,
                                                },
                                                {
                                                    height: 1562,
                                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-Android-2.png',
                                                    width: 2778,
                                                },
                                            ],
                                        },
                                    ],
                                },
                                {
                                    displayLocale: 'European Spanish',
                                    locale: 'es-ES',
                                    screenshotSets: [
                                        {
                                            screenshotDisplayType: 'phoneScreenshots',
                                            screenshots: [
                                                {
                                                    height: 1562,
                                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-Android-1.png',
                                                    width: 2778,
                                                },
                                                {
                                                    height: 1562,
                                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-Android-2.png',
                                                    width: 2778,
                                                },
                                            ],
                                        },
                                    ],
                                },
                            ],
                            storeUrl: 'https://play.google.com/console',
                        },
                        groupIndex: 2,
                        orderIndex: 0,
                        checklistItems: [
                            {
                                id: 'c03218kcu89v1j3i28cg',
                                title: 'Upload screenshots to Google play per the guidelines',
                                description: null,
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'screenshots',
                                approverGroups: ['design'],
                                releaseTypes: 'all',
                                notifyEnabled: false,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: androidStepAutomations['screenshots'],
                    },
                    {
                        id: '1.0.0#metadata',
                        type: 'metadata',
                        description: 'Metadata',
                        status: 'pending',
                        statusReasonString: 'Waiting for manual approval',
                        blockingConfig: 'NonBlocking',
                        data: {
                            isApproved: false,
                            localizations: [
                                {
                                    description: null,
                                    displayLocale: 'American English',
                                    id: '1.25.0:en-US',
                                    keywords: null,
                                    locale: 'en-US',
                                    promotionalText: null,
                                    status: 'ready',
                                    whatsNew:
                                        "Bug fixes and improvements... we're always working hard to build a better app for you!",
                                },
                                {
                                    description: null,
                                    displayLocale: 'European Spanish',
                                    id: '1.25.0:es-ES',
                                    keywords: null,
                                    locale: 'es-ES',
                                    promotionalText: null,
                                    status: 'ready',
                                    whatsNew:
                                        "Bug fixes and improvements... we're always working hard to build a better app for you!",
                                },
                            ],
                            requiresApproval: true,
                            storeUrl: 'https://play.google.com/console',
                        },
                        groupIndex: 2,
                        orderIndex: 1,
                        checklistItems: [],
                        automationRuns: androidStepAutomations['metadata'],
                    },
                    {
                        id: '1.0.0#approvals',
                        type: 'approvals',
                        description: 'Approvals',
                        status: 'pending',
                        statusReasonString: 'There are 2 items that still require approval.',
                        blockingConfig: 'Blocking',
                        data: {
                            approvalItems: [
                                {
                                    approverGroups: ['pm'],
                                    comment: null,
                                    description: null,
                                    id: 'c8to4mu9f6i9qtljdkjg',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: {
                                        createdAt: '2022-03-23T20:26:38.120635796Z',
                                        createdBy: 'aearhart@demo.com',
                                        status: 'approved',
                                    },
                                    statuses: ['inProgress', 'blocked', 'rejected', 'approved'],
                                    title: 'Approval from Engagement team',
                                    type: 'multi',
                                    owners: ['aearhart@demo.com'],
                                },
                                {
                                    approverGroups: ['engineer'],
                                    comment: null,
                                    description: null,
                                    id: 'c8to4q69f6i9qtljdkk0',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: null,
                                    statuses: ['inProgress', 'blocked', 'rejected', 'approved'],
                                    title: 'Approval from Acquisitions team',
                                    type: 'multi',
                                    owners: ['narmstrong@demo.com', 'ssullenberger@demo.com'],
                                },
                                {
                                    approverGroups: ['pm'],
                                    comment: null,
                                    description: null,
                                    id: 'c8to4t69f6i9qtljdkkg',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: null,
                                    statuses: ['inProgress', 'blocked', 'rejected', 'approved'],
                                    title: 'Approval from Payments team',
                                    type: 'multi',
                                    owners: [],
                                },
                            ],
                        },
                        groupIndex: 3,
                        orderIndex: 0,
                        checklistItems: [],
                        automationRuns: androidStepAutomations['approvals'],
                    },
                    {
                        id: '1.0.0#submission',
                        type: 'submission',
                        description: 'Submission',
                        status: 'pending',
                        statusReasonString: 'Ready to submit.',
                        blockingConfig: 'Blocking',
                        data: {
                            appStoreData: {
                                allInfoPresentForSubmission: true,
                                integrationId: 'google',
                                phasedRelease: {
                                    isManuallyAcceleratedPhasedRollout: false,
                                    isRunwayAcceleratedRollout: false,
                                    isRunwayPaused: false,
                                    state: 'draft',
                                    userFraction: 0,
                                },
                                state: 'draft',
                            },
                            storeUrl: 'https://play.google.com/console',
                            submittedAt: null,
                            workflowData: null,
                        },
                        groupIndex: 4,
                        orderIndex: 0,
                        checklistItems: [
                            {
                                id: 'c5n2h7ln86bo9gnibnr0',
                                title: 'Thing to do before submit',
                                description: 'Do this!',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'submission',
                                approverGroups: ['approver'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'c8al5bdn86bs44bjrpd0',
                                title: 'Another thing to remember before submitting',
                                description: 'All this stuff',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'submission',
                                approverGroups: ['pilot', 'pm'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'c8e0r55n86boufv00ong',
                                title: 'Important Pre-submit step',
                                description: 'Remember to do this before submitting.',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'submission',
                                approverGroups: ['pilot', 'pm'],
                                releaseTypes: 'all',
                                notifyEnabled: false,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: androidStepAutomations['submission'],
                    },
                    {
                        id: '1.0.0#storeReview',
                        type: 'storeReview',
                        description: 'App store review',
                        status: 'inactive',
                        statusReasonString: 'Waiting for submission.',
                        blockingConfig: 'Blocking',
                        data: {
                            appStoreData: {
                                allInfoPresentForSubmission: true,
                                integrationId: 'google',
                                phasedRelease: {
                                    isManuallyAcceleratedPhasedRollout: false,
                                    isRunwayAcceleratedRollout: false,
                                    isRunwayPaused: false,
                                    state: 'draft',
                                    userFraction: 0,
                                },
                                state: 'draft',
                            },
                            approvedAt: null,
                            devRejectedAt: null,
                            startedReviewAt: null,
                            storeRejectedAt: null,
                            storeUrl: 'https://play.google.com/console',
                            submittedAt: null,
                        },
                        groupIndex: 4,
                        orderIndex: 1,
                        checklistItems: [
                            {
                                id: 'c7oomvdn86bpp9lejr1g',
                                title: 'Release via managed publishing if needed',
                                description: null,
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'storeReview',
                                approverGroups: ['pilot'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'c59kpldachdcretdibcg',
                                title: 'Many different users can deal with this',
                                description: 'engineers, qa and pms should be able to approve this',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'storeReview',
                                approverGroups: ['engineer', 'qa', 'pm'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: androidStepAutomations['storeReview'],
                    },
                    {
                        id: '1.0.0#takeoff',
                        type: 'takeoff',
                        description: 'Release',
                        status: 'inactive',
                        statusReasonString: 'Waiting for submission.',
                        blockingConfig: 'Blocking',
                        data: {
                            appStoreData: {
                                allInfoPresentForSubmission: true,
                                integrationId: 'google',
                                phasedRelease: {
                                    isManuallyAcceleratedPhasedRollout: false,
                                    isRunwayAcceleratedRollout: false,
                                    isRunwayPaused: false,
                                    state: 'draft',
                                    userFraction: 0,
                                },
                                state: 'draft',
                            },
                            appStoreUrl: 'https://play.google.com/store/apps/details?id=com.runway.example',
                            storeUrl: 'https://play.google.com/console',
                            tagData: {
                                codeDiffUrl: 'https://github.com',
                                expectedReleaseTagName: 'v1.25.0',
                                releaseTag: null,
                                releaseTagState: 'notTaggable',
                                taggableCommits: [],
                            },
                        },
                        groupIndex: 4,
                        orderIndex: 2,
                        checklistItems: [
                            {
                                id: 'bvv18nkfoo7cohhp7ju0',
                                title: 'Send announcement email to team@ distribution list',
                                description: null,
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'takeoff',
                                approverGroups: ['pilot'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: androidStepAutomations['takeoff'],
                    },
                ]
            :   [
                    {
                        id: '1.0.0#kickoff',
                        type: 'kickoff',
                        description: 'Kickoff',
                        status: 'ready',
                        statusReasonString: 'Kicked off!',
                        blockingConfig: 'Blocking',
                        data: {
                            appUsesStaticReleaseBranch: true,
                            bumpVersionStatus: 'detected',
                            canPerformBumpVersion: true,
                            canPerformCodePromotion: true,
                            codePromotionPullRequest: {
                                id: '1234',
                                number: 119,
                                status: 'merged',
                                title: 'Merge development -\u003e main',
                                url: 'https://github.com/runway/demo-ios/pull/119',
                            },
                            codePromotionStatus: 'detected',
                            doesReleaseBranchExist: true,
                            hasPerformedAnyCodePromotion: true,
                            hotfixBranchingBase: null,
                            previousReleaseTag: 'v1.0.0',
                            previousReleaseVersion: '1.0.0',
                            previousTaggedReleaseTag: 'v1.0.0',
                            previousTaggedReleaseTagVersionsBehind: 0,
                            releaseBranch: 'main',
                            releaseBranchCodeVersionData: {
                                branchName: 'main',
                                bumpVersionPullRequest: null,
                                bumpVersionStatus: 'detected',
                                canPerformBumpVersion: false,
                                currentCodeVersionExternalString: '1.0.0',
                                targetCodeVersionExternalString: '1.0.0',
                                targetCodeVersionInternalString: '1.0.0',
                            },
                            workingBranchCodeVersionData: {
                                branchName: 'master',
                                bumpVersionPullRequest: {
                                    id: 'PR_123',
                                    number: 96,
                                    pullRequestType: 'BumpVersion',
                                    status: 'merged',
                                    title: 'Bumping version to 1.0.0',
                                    url: 'https://github.com/runway-org/fake-app/pull/96',
                                },
                                bumpVersionStatus: 'merged',
                                canPerformBumpVersion: false,
                                currentCodeVersionExternalString: '1.0.0',
                                targetCodeVersionExternalString: '1.0.0',
                                targetCodeVersionInternalString: '1.0.0',
                            },
                            releaseBranchDiffUrl: 'https://github.com/runway/demo-ios/compare',
                            releaseBranchUrl: 'https://github.com/runway/demo-ios/tree/main',
                            resolvedFeatureAffiliations: [
                                {
                                    affiliationType: 'jira-fix-version',
                                    integrationId: 'jira',
                                    resolvedString: '1.0.0',
                                },
                                {
                                    affiliationType: 'jira-label',
                                    integrationId: 'jira',
                                    resolvedString: 'release-1.0.0',
                                },
                            ],
                            workingBranchDiffUrl: 'https://github.com/runway/demo-ios/compare',
                        },
                        groupIndex: 0,
                        orderIndex: 0,
                        checklistItems: [
                            {
                                id: 'abc',
                                title: 'Pre-kickoff reminders',
                                description: 'Description   \u003cb\u003eyo\u003c/b\u003e !!',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'kickoff',
                                approverGroups: ['pilot'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: {
                                    status: 'approved',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'def',
                                title: 'Send out reminder for upcoming release deadlines',
                                description: 'Some detail',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'kickoff',
                                approverGroups: ['pilot'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: {
                                    status: 'approved',
                                    createdAt: '2022-03-03T18:03:32.236911544Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                comment: {
                                    comment: 'Reminder sent via email',
                                    createdAt: '2022-03-03T18:00:32.236911544Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'jkl',
                                title: 'Verify versioning with product team',
                                description: null,
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'kickoff',
                                approverGroups: [],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: {
                                    status: 'approved',
                                    createdAt: '2022-03-03T18:03:33.133516479Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: iosStepAutomations['kickoff'],
                    },
                    {
                        id: '1.0.0#featureReadiness',
                        type: 'featureReadiness',
                        description: 'Feature readiness',
                        status: 'pending',
                        statusReasonString: 'Some work for this release is not yet complete.',
                        blockingConfig: 'Blocking',
                        data: { items: null, percentComplete: 0, workingBranchPercentComplete: null },
                        groupIndex: 0,
                        orderIndex: 1,
                        checklistItems: [
                            {
                                id: 'abc',
                                title: 'Verify production feature flags are correct for this release',
                                description: null,
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'featureReadiness',
                                approverGroups: ['engineer'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: {
                                    status: 'approved',
                                    createdAt: '2022-03-15T22:54:38.462070296Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'def',
                                title: 'Update CMS version flags for this release',
                                description: '',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'featureReadiness',
                                approverGroups: [],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: {
                                    status: 'approved',
                                    createdAt: '2022-03-15T22:54:39.472656772Z',
                                    createdBy: 'aearhart@demo.com',
                                },
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: iosStepAutomations['featureReadiness'],
                    },
                    {
                        id: '1.0.0#releaseCandidate',
                        type: 'releaseCandidate',
                        description: 'CI',
                        status: 'ready',
                        statusReasonString: 'CI build succeeded.',
                        blockingConfig: 'Blocking',
                        data: {
                            builds: [
                                {
                                    associatedAppStoreBuild: {
                                        betaBuildDetails: {
                                            autoNotifyEnabled: true,
                                            externalBuildState: 'IN_BETA_TESTING',
                                            internalBuildState: 'IN_BETA_TESTING',
                                        },
                                        buildNumber: '298',
                                        id: '123',
                                        integrationId: 'apple',
                                        isProcessed: true,
                                        processingState: 'VALID',
                                        type: 'ios-binary',
                                        uploadedAt: '2022-03-15T12:31:21-07:00',
                                    },
                                    ciBuild: {
                                        artifacts: [],
                                        branch: 'main',
                                        buildFinishedAt: '2022-03-21T17:32:25Z',
                                        buildHash: 'zcebLLhLwk8m-GonPSg84G6omXw=',
                                        buildIdentifier: '298',
                                        buildStartedAt: '2022-03-21T17:23:53Z',
                                        buildStatus: 'success',
                                        buildUrl: 'https://app.bitrise.io/build/abc',
                                        commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                        commitAuthor: 'runway-github[bot]',
                                        commitMessage:
                                            'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main`.',
                                        integrationId: 'bitrise',
                                        providerBuildStatusString: 'Success',
                                        workflowRunId: 'abc',
                                    },
                                    isSelectedBuild: true,
                                    selectedReason: 'autoRunwaySelection',
                                },
                                {
                                    associatedAppStoreBuild: {
                                        betaBuildDetails: {
                                            autoNotifyEnabled: true,
                                            externalBuildState: 'IN_BETA_TESTING',
                                            internalBuildState: 'IN_BETA_TESTING',
                                        },
                                        buildNumber: '297',
                                        id: '123',
                                        integrationId: 'apple',
                                        isProcessed: true,
                                        processingState: 'VALID',
                                        type: 'ios-binary',
                                        uploadedAt: '2022-03-15T12:31:21-07:00',
                                    },
                                    ciBuild: {
                                        artifacts: [
                                            {
                                                downloadUrl: 'https://demo.runway.team',
                                                fileName: 'app.dSYM.zip',
                                                size: 16,
                                            },
                                            {
                                                downloadUrl: 'https://demo.runway.team',
                                                fileName: 'app.ipa',
                                                size: 139,
                                            },
                                        ],
                                        branch: 'main',
                                        buildFinishedAt: '2022-03-15T19:28:41Z',
                                        buildHash: '123=',
                                        buildIdentifier: '297',
                                        buildStartedAt: '2022-03-15T19:17:48Z',
                                        buildStatus: 'success',
                                        buildUrl: 'https://app.bitrise.io/build/123',
                                        commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                        commitAuthor: 'runway-github[bot]',
                                        commitMessage:
                                            'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main`',
                                        integrationId: 'bitrise',
                                        providerBuildStatusString: 'Success',
                                        workflowRunId: '123',
                                    },
                                    isSelectedBuild: false,
                                    selectedReason: 'autoRunwaySelection',
                                },
                                {
                                    associatedAppStoreBuild: {
                                        betaBuildDetails: {
                                            autoNotifyEnabled: true,
                                            externalBuildState: 'IN_BETA_TESTING',
                                            internalBuildState: 'IN_BETA_TESTING',
                                        },
                                        buildNumber: '296',
                                        id: 'abc',
                                        integrationId: 'apple',
                                        isProcessed: true,
                                        processingState: 'VALID',
                                        type: 'ios-binary',
                                        uploadedAt: '2022-03-15T11:50:03-07:00',
                                    },
                                    ciBuild: {
                                        artifacts: [
                                            {
                                                downloadUrl: 'https://demo.runway.team',
                                                fileName: 'app.ipa',
                                                size: 139,
                                            },
                                            {
                                                downloadUrl: 'https://demo.runway.team',
                                                fileName: 'app.dSYM.zip',
                                                size: 16,
                                            },
                                        ],
                                        branch: 'main',
                                        buildFinishedAt: '2022-03-15T18:46:11Z',
                                        buildHash: 'abcd',
                                        buildIdentifier: '296',
                                        buildStartedAt: '2022-03-15T18:35:29Z',
                                        buildStatus: 'success',
                                        buildUrl: 'https://app.bitrise.io/build/abc',
                                        commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                        commitAuthor: 'runway-github[bot]',
                                        commitMessage:
                                            'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main`.',
                                        integrationId: 'bitrise',
                                        providerBuildStatusString: 'Success',
                                        workflowRunId: 'abc-abc-abc',
                                    },
                                    isSelectedBuild: false,
                                    selectedReason: 'autoRunwaySelection',
                                },
                                {
                                    associatedAppStoreBuild: null,
                                    ciBuild: {
                                        artifacts: [],
                                        branch: 'main',
                                        buildFinishedAt: '2022-03-15T18:35:29Z',
                                        buildHash: 'def',
                                        buildIdentifier: '295',
                                        buildStartedAt: '2022-03-15T18:33:17Z',
                                        buildStatus: 'stopped',
                                        buildUrl: 'https://app.bitrise.io/build/def',
                                        commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                        commitAuthor: 'runway-github[bot]',
                                        commitMessage:
                                            'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main`.',
                                        integrationId: 'bitrise',
                                        providerBuildStatusString: 'Stopped',
                                        workflowRunId: '123-123-123',
                                    },
                                    isSelectedBuild: false,
                                    selectedReason: 'autoRunwaySelection',
                                },
                                {
                                    associatedAppStoreBuild: {
                                        betaBuildDetails: {
                                            autoNotifyEnabled: true,
                                            externalBuildState: 'IN_BETA_TESTING',
                                            internalBuildState: 'IN_BETA_TESTING',
                                        },
                                        buildNumber: '290',
                                        id: 'bef',
                                        integrationId: 'apple',
                                        isProcessed: true,
                                        processingState: 'VALID',
                                        type: 'ios-binary',
                                        uploadedAt: '2022-03-11T12:08:29-08:00',
                                    },
                                    ciBuild: {
                                        artifacts: [
                                            {
                                                downloadUrl: 'https://demo.runway.team',
                                                fileName: 'app.dSYM.zip',
                                                size: 16,
                                            },
                                            {
                                                downloadUrl: 'https://demo.runway.team',
                                                fileName: 'app.ipa',
                                                size: 139,
                                            },
                                        ],
                                        branch: 'main',
                                        buildFinishedAt: '2022-03-11T20:05:46Z',
                                        buildHash: 'cya',
                                        buildIdentifier: '290',
                                        buildStartedAt: '2022-03-11T19:55:17Z',
                                        buildStatus: 'success',
                                        buildUrl: 'https://app.bitrise.io/build/123',
                                        commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                        commitAuthor: 'runway-github[bot]',
                                        commitMessage:
                                            'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main` for release.',
                                        integrationId: 'bitrise',
                                        providerBuildStatusString: 'Success',
                                        workflowRunId: 'cya-cya',
                                    },
                                    isSelectedBuild: false,
                                    selectedReason: 'autoRunwaySelection',
                                },
                                {
                                    associatedAppStoreBuild: {
                                        betaBuildDetails: {
                                            autoNotifyEnabled: true,
                                            externalBuildState: 'IN_BETA_TESTING',
                                            internalBuildState: 'IN_BETA_TESTING',
                                        },
                                        buildNumber: '289',
                                        id: '123',
                                        integrationId: 'apple',
                                        isProcessed: true,
                                        processingState: 'VALID',
                                        type: 'ios-binary',
                                        uploadedAt: '2022-03-10T08:20:05-08:00',
                                    },
                                    ciBuild: {
                                        artifacts: [
                                            {
                                                downloadUrl: 'https://demo.runway.team',
                                                fileName: 'app.dSYM.zip',
                                                size: 16,
                                            },
                                            {
                                                downloadUrl: 'https://demo.runway.team',
                                                fileName: 'app.ipa',
                                                size: 139,
                                            },
                                        ],
                                        branch: 'main',
                                        buildFinishedAt: '2022-03-10T16:16:53Z',
                                        buildHash: '123-abc',
                                        buildIdentifier: '289',
                                        buildStartedAt: '2022-03-10T16:06:14Z',
                                        buildStatus: 'success',
                                        buildUrl: 'https://app.bitrise.io/build/123-abc',
                                        commit: '0facaf1063bfa427b4bb1b5dba5b35d662c3bf45',
                                        commitAuthor: 'runway-github[bot]',
                                        commitMessage:
                                            'Merge pull request #118 from demo/development\n\nRunway promoted branch `development` to `main`.',
                                        integrationId: 'bitrise',
                                        providerBuildStatusString: 'Success',
                                        workflowRunId: '123-abc',
                                    },
                                    isSelectedBuild: false,
                                    selectedReason: 'autoRunwaySelection',
                                },
                                {
                                    associatedAppStoreBuild: {
                                        betaBuildDetails: {
                                            autoNotifyEnabled: true,
                                            externalBuildState: 'IN_BETA_TESTING',
                                            internalBuildState: 'IN_BETA_TESTING',
                                        },
                                        buildNumber: '284',
                                        id: '123',
                                        integrationId: 'apple',
                                        isProcessed: true,
                                        processingState: 'VALID',
                                        type: 'ios-binary',
                                        uploadedAt: '2022-03-03T10:09:08-08:00',
                                    },
                                    ciBuild: {
                                        artifacts: [
                                            {
                                                downloadUrl: 'https://demo.runway.team',
                                                fileName: 'tab.ipa',
                                                size: 139,
                                            },
                                            {
                                                downloadUrl: 'https://demo.runway.team',
                                                fileName: 'tab.dSYM.zip',
                                                size: 16,
                                            },
                                        ],
                                        branch: 'main',
                                        buildFinishedAt: '2022-03-03T18:05:50Z',
                                        buildHash: 'ux-OXdNnKuSGcQGoUxWVzOmXmBk=',
                                        buildIdentifier: '284',
                                        buildStartedAt: '2022-03-03T17:55:15Z',
                                        buildStatus: 'success',
                                        buildUrl: 'https://app.bitrise.io/build/123',
                                        commit: '0facaf1063bfa427b4bb1b5dba5b35d662c3bf45',
                                        commitAuthor: 'runway-github[bot]',
                                        commitMessage:
                                            'Merge pull request #118 from demo/development\n\nRunway promoted branch `development` to `main` for release 3.97.0.',
                                        integrationId: 'bitrise',
                                        providerBuildStatusString: 'Success',
                                        workflowRunId: 'abc-def-hij',
                                    },
                                    isSelectedBuild: false,
                                    selectedReason: 'autoRunwaySelection',
                                },
                            ],
                            workflowData: {
                                branch: 'main',
                                headCommit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                headCommitAuthor: 'runway-github[bot]',
                                headCommitMessage:
                                    'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main` for release 3.97.0.',
                                integrationType: 'bitrise',
                                workflowName: 'deploy',
                                workflowType: 'staging',
                                workflowUrl: 'https://app.bitrise.io/app/123/workflow_editor',
                            },
                        },
                        groupIndex: 1,
                        orderIndex: 0,
                        checklistItems: [],
                        automationRuns: iosStepAutomations['releaseCandidate'],
                    },
                    {
                        id: '1.0.0#regressionTesting',
                        type: 'regressionTesting',
                        description: 'Regression testing',
                        status: 'pending',
                        statusReasonString: 'Regression testing in progress...',
                        blockingConfig: 'Blocking',
                        data: {
                            availableBuilds: [
                                {
                                    appStoreBuildIdentifier: '297',
                                    appStoreIntegrationId: 'apple',
                                    buildIdentifier: '297',
                                    hash: 'A5LZuDfhn3e6WNweXBzV8AR1w6c=',
                                    integrationId: 'bitrise',
                                },
                                {
                                    appStoreBuildIdentifier: '296',
                                    appStoreIntegrationId: 'apple',
                                    buildIdentifier: '296',
                                    hash: '0qVMy0jBTbwzL5k67ZzU092K-wA=',
                                    integrationId: 'bitrise',
                                },
                                {
                                    appStoreBuildIdentifier: '290',
                                    appStoreIntegrationId: 'apple',
                                    buildIdentifier: '290',
                                    hash: 'cYAkkfz3H8wQ_0fBrtp05sJ6uMA=',
                                    integrationId: 'bitrise',
                                },
                                {
                                    appStoreBuildIdentifier: '289',
                                    appStoreIntegrationId: 'apple',
                                    buildIdentifier: '289',
                                    hash: '2LLiuHxPPvmLOnRjj8SzN86Gx54=',
                                    integrationId: 'bitrise',
                                },
                                {
                                    appStoreBuildIdentifier: '284',
                                    appStoreIntegrationId: 'apple',
                                    buildIdentifier: '284',
                                    hash: 'ux-OXdNnKuSGcQGoUxWVzOmXmBk=',
                                    integrationId: 'bitrise',
                                },
                            ],
                            regressionBuildInfo: {
                                associatedAppStoreBuild: {
                                    betaBuildDetails: {
                                        autoNotifyEnabled: true,
                                        externalBuildState: 'IN_BETA_TESTING',
                                        internalBuildState: 'IN_BETA_TESTING',
                                    },
                                    buildNumber: '297',
                                    id: 'fdeaddb9-87af-446b-baff-91eb85d1446c',
                                    integrationId: 'apple',
                                    isProcessed: true,
                                    processingState: 'VALID',
                                    type: 'ios-binary',
                                    uploadedAt: '2022-03-15T12:31:21-07:00',
                                },
                                ciBuild: {
                                    artifacts: null,
                                    branch: 'main',
                                    buildFinishedAt: '2022-03-15T19:28:41Z',
                                    buildHash: 'A5LZuDfhn3e6WNweXBzV8AR1w6c=',
                                    buildIdentifier: '297',
                                    buildStartedAt: '2022-03-15T19:17:48Z',
                                    buildStatus: 'success',
                                    buildUrl: 'https://app.bitrise.io/build/123',
                                    commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                    commitAuthor: 'runway-github[bot]',
                                    commitMessage:
                                        'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main` for release 3.97.0.',
                                    integrationId: 'bitrise',
                                    providerBuildStatusString: 'Success',
                                    workflowRunId: '0a65a37e-ba7d-45d7-bd3f-3ec0ba117bd8',
                                },
                            },
                            status: 'inProgress',
                            testItems: [
                                {
                                    approverGroups: ['qa'],
                                    comment: {
                                        comment: 'Tested sign in with google, github, apple, all look good',
                                        createdAt: '2022-02-27T23:00:20.886428625Z',
                                        createdBy: 'ssullenberger@demo.com',
                                    },
                                    description: null,
                                    id: 'c55n1kdachdaceuvmb00',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: {
                                        status: 'passed',
                                        createdAt: '2022-02-27T23:05:20.886428625Z',
                                        createdBy: 'ssullenberger@demo.com',
                                    },
                                    statuses: ['inProgress', 'blocked', 'failed', 'passed'],
                                    title: 'Test login flow',
                                    type: 'multi',
                                    owners: ['ssullenberger@demo.com'],
                                },
                                {
                                    approverGroups: ['qa'],
                                    comment: null,
                                    description: null,
                                    id: 'c8kfkuln86busgdit4ag',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: {
                                        status: 'inProgress',
                                        createdAt: '2022-02-27T23:05:20.886428625Z',
                                        createdBy: 'aearhart@demo.com',
                                    },
                                    statuses: ['inProgress', 'blocked', 'failed', 'passed'],
                                    title: 'Test "Add receipts" flow',
                                    type: 'multi',
                                    owners: ['narmstrong@demo.com'],
                                },
                                {
                                    approverGroups: ['qa'],
                                    comment: null,
                                    description: null,
                                    id: 'c55n1p5achdaceuvmb0g',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: {
                                        status: 'passed',
                                        createdAt: '2022-02-27T23:05:20.886428625Z',
                                        createdBy: 'aearhart@demo.com',
                                    },
                                    statuses: ['inProgress', 'blocked', 'failed', 'passed'],
                                    title: 'Test checkout flow',
                                    type: 'multi',
                                    owners: [],
                                },
                                {
                                    approverGroups: ['qa'],
                                    comment: null,
                                    description: null,
                                    id: 'c55n25lachdaceuvmb1g',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: null,
                                    statuses: ['inProgress', 'blocked', 'failed', 'passed'],
                                    title: 'Test "Share with friends" flow',
                                    type: 'multi',
                                    owners: [],
                                },
                                {
                                    approverGroups: ['qa'],
                                    comment: null,
                                    description: 'Remember to test this stuff right now',
                                    id: 'c8efiddn86bppsoltosg',
                                    notifyEnabled: false,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: {
                                        status: 'failed',
                                        createdAt: '2022-02-27T23:05:20.886428625Z',
                                        createdBy: 'aearhart@demo.com',
                                    },
                                    statuses: ['inProgress', 'blocked', 'failed', 'passed'],
                                    title: 'Another thing to test',
                                    type: 'multi',
                                    owners: [],
                                },
                            ],
                            nonHistoricalTestRunsCompletionFraction: 0.95,
                            nonHistoricalTestRunsStatusesTotalCount: 30,
                            testRuns: [
                                {
                                    completedAt: null,
                                    completedCount: 19,
                                    createdAt: '2022-02-17T23:48:12Z',
                                    description: '',
                                    id: '1',
                                    integrationId: 'testrail',
                                    isInProgress: true,
                                    name: 'Checkout Pathways',
                                    statuses: [
                                        { color: '6EC78B', count: 19, displayableName: 'Passed', fraction: 0.95 },
                                        { color: '595959', count: 0, displayableName: 'Blocked', fraction: 0 },
                                        { color: 'd9d9d9', count: 1, displayableName: 'Untested', fraction: 0.05 },
                                        { color: 'F4AD22', count: 0, displayableName: 'Retest', fraction: 0 },
                                        { color: 'EF2D56', count: 0, displayableName: 'Failed', fraction: 0 },
                                    ],
                                    totalCount: 20,
                                    updatedAt: '2022-02-17T23:52:10Z',
                                    url: 'https://demo.testrail.io',
                                    completionFraction: 0.95,
                                    isHistorical: false,
                                },
                                {
                                    completedAt: null,
                                    completedCount: 9,
                                    createdAt: '2022-02-17T23:48:12Z',
                                    description: '',
                                    id: '1',
                                    integrationId: 'testrail',
                                    isInProgress: true,
                                    name: 'Core Pathways',
                                    statuses: [
                                        { color: '6EC78B', count: 9, displayableName: 'Passed', fraction: 0.55 },
                                        { color: '595959', count: 1, displayableName: 'Blocked', fraction: 0.05 },
                                        { color: 'd9d9d9', count: 5, displayableName: 'Untested', fraction: 0.3 },
                                        { color: 'F4AD22', count: 1, displayableName: 'Retest', fraction: 0.05 },
                                        { color: 'EF2D56', count: 2, displayableName: 'Failed', fraction: 0.1 },
                                    ],
                                    totalCount: 18,
                                    updatedAt: '2022-02-17T23:52:10Z',
                                    url: 'https://demo.testrail.io',
                                    completionFraction: 0.55,
                                    isHistorical: false,
                                },
                                {
                                    completedAt: '2022-02-18T23:48:12Z',
                                    completedCount: 18,
                                    createdAt: '2022-02-17T23:48:12Z',
                                    description: '',
                                    id: '1',
                                    integrationId: 'testrail',
                                    isInProgress: false,
                                    name: 'Core pathways',
                                    statuses: [
                                        { color: '6EC78B', count: 10, displayableName: 'Passed', fraction: 1 },
                                        { color: '595959', count: 0, displayableName: 'Blocked', fraction: 0 },
                                        { color: 'd9d9d9', count: 0, displayableName: 'Untested', fraction: 0 },
                                        { color: 'F4AD22', count: 0, displayableName: 'Retest', fraction: 0 },
                                        { color: 'EF2D56', count: 0, displayableName: 'Failed', fraction: 0 },
                                    ],
                                    totalCount: 18,
                                    updatedAt: '2022-02-17T23:52:10Z',
                                    url: 'https://demo.testrail.io',
                                    completionFraction: 1,
                                    isHistorical: true,
                                },
                                {
                                    completedAt: '2022-02-18T23:48:12Z',
                                    completedCount: 18,
                                    createdAt: '2022-02-17T23:48:12Z',
                                    description: '',
                                    id: '1',
                                    integrationId: 'testrail',
                                    isInProgress: false,
                                    name: 'New user pathway',
                                    statuses: [
                                        { color: '6EC78B', count: 10, displayableName: 'Passed', fraction: 1 },
                                        { color: '595959', count: 0, displayableName: 'Blocked', fraction: 0 },
                                        { color: 'd9d9d9', count: 0, displayableName: 'Untested', fraction: 0 },
                                        { color: 'F4AD22', count: 0, displayableName: 'Retest', fraction: 0 },
                                        { color: 'EF2D56', count: 0, displayableName: 'Failed', fraction: 0 },
                                    ],
                                    totalCount: 18,
                                    updatedAt: '2022-02-17T23:52:10Z',
                                    url: 'https://demo.testrail.io',
                                    completionFraction: 1,
                                    isHistorical: true,
                                },
                            ],
                            updatedAt: '2022-03-15T18:57:32.629493-04:00',
                        },
                        groupIndex: 1,
                        orderIndex: 1,
                        checklistItems: [],
                        automationRuns: iosStepAutomations['regressionTesting'],
                    },
                    {
                        id: '1.0.0#betaTesting',
                        type: 'betaTesting',
                        description: 'Beta testing',
                        status: 'ready',
                        statusReasonString: 'Beta testing completed!',
                        blockingConfig: 'Blocking',
                        data: {
                            betaBuilds: [
                                {
                                    betaGroups: [
                                        '6c1c4439-16cb-49e8-b7c4-97d7c1f46a51',
                                        '7874adb9-9af0-427f-aabd-f7d36bed9049',
                                    ],
                                    betaStatus: 'testing',
                                    betaTesters: ['aearhart@demo.com', 'charles@demo.com', 'jimmy@demo.com'],
                                    betaTestingNotes: [
                                        {
                                            displayLocale: 'American English',
                                            id: 'cc6e2e20-0b62-441e-b755-594bed74b9e6',
                                            locale: 'en-US',
                                            notes: 'Test bug fixes on product page, checkout new design.',
                                        },
                                    ],
                                    buildId: '475bf507-c3c4-443f-9845-932f16748ec7',
                                    appStoreBuild: {
                                        appStoreBuild: {
                                            buildNumber: '298',
                                            id: '475bf507-c3c4-443f-9845-932f16748ec7',
                                            integrationId: 'apple-beta',
                                            isProcessed: true,
                                            processingState: 'VALID',
                                            type: 'ios-binary',
                                            uploadedAt: '2022-03-15T18:07:07-07:00',
                                        },
                                        associatedCIBuild: {
                                            branch: 'main',
                                            buildFinishedAt: '2022-03-15T18:46:11Z',
                                            buildHash: '0qVMy0jBTbwzL5k67ZzU092K-wA=',
                                            buildIdentifier: '298',
                                            buildStartedAt: '2022-03-15T18:35:29Z',
                                            buildStatus: 'success',
                                            buildUrl: 'https://app.bitrise.io/build/123',
                                            commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                            commitAuthor: null,
                                            commitMessage:
                                                'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main` for release 3.97.0.',
                                            integrationId: 'bitrise',
                                            providerBuildStatusString: 'Success',
                                            workflowRunId: '482375f7-5c67-46a0-b3f3-0e24907c2e3c',
                                        },
                                        stabilityInfos: {},
                                    },
                                    betaBuildDetails: {
                                        autoNotifyEnabled: true,
                                        externalBuildState: 'IN_BETA_TESTING',
                                        internalBuildState: 'IN_BETA_TESTING',
                                    },
                                    isConsideredExternal: true,
                                    isLatestBuild: true,
                                    isSelectedBuild: true,
                                    metrics: { numInstalls: 0 },
                                    selectedReason: 'autoRunwaySelection',
                                },
                                {
                                    betaGroups: [
                                        '6c1c4439-16cb-49e8-b7c4-97d7c1f46a51',
                                        '7874adb9-9af0-427f-aabd-f7d36bed9049',
                                    ],
                                    betaStatus: 'testing',
                                    betaTesters: [],
                                    betaTestingNotes: [
                                        {
                                            displayLocale: 'American English',
                                            id: 'ea1599fb-b230-4966-b044-5f2ac9be42ab',
                                            locale: 'en-US',
                                            notes: '',
                                        },
                                    ],
                                    betaBuildDetails: {
                                        autoNotifyEnabled: true,
                                        externalBuildState: 'IN_BETA_TESTING',
                                        internalBuildState: 'IN_BETA_TESTING',
                                    },
                                    appStoreBuild: {
                                        appStoreBuild: {
                                            buildNumber: '297',
                                            id: 'fdeaddb9-87af-446b-baff-91eb85d1446c',
                                            integrationId: 'apple-beta',
                                            isProcessed: true,
                                            processingState: 'VALID',
                                            type: 'ios-binary',
                                            uploadedAt: '2022-03-15T12:31:21-07:00',
                                        },
                                        associatedCIBuild: {
                                            branch: 'main',
                                            buildFinishedAt: '2022-03-15T19:28:41Z',
                                            buildHash: 'A5LZuDfhn3e6WNweXBzV8AR1w6c=',
                                            buildIdentifier: '297',
                                            buildStartedAt: '2022-03-15T19:17:48Z',
                                            buildStatus: 'success',
                                            buildUrl: 'https://app.bitrise.io/build/123',
                                            commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                            commitAuthor: null,
                                            commitMessage:
                                                'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main` for release 3.97.0.',
                                            integrationId: 'bitrise',
                                            providerBuildStatusString: 'Success',
                                            workflowRunId: '0a65a37e-ba7d-45d7-bd3f-3ec0ba117bd8',
                                        },
                                    },
                                    isConsideredExternal: true,
                                    isLatestBuild: false,
                                    isSelectedBuild: false,
                                    metrics: { numInstalls: 0 },
                                    selectedReason: '',
                                },
                                {
                                    betaGroups: [
                                        '6c1c4439-16cb-49e8-b7c4-97d7c1f46a51',
                                        '7874adb9-9af0-427f-aabd-f7d36bed9049',
                                    ],
                                    betaStatus: 'testing',
                                    betaTesters: [],
                                    betaTestingNotes: [
                                        {
                                            displayLocale: 'American English',
                                            id: 'c4fad929-20ef-4a3e-adc8-58425d2475d1',
                                            locale: 'en-US',
                                            notes: '',
                                        },
                                    ],
                                    betaBuildDetails: {
                                        autoNotifyEnabled: true,
                                        externalBuildState: 'IN_BETA_TESTING',
                                        internalBuildState: 'IN_BETA_TESTING',
                                    },
                                    appStoreBuild: {
                                        appStoreBuild: {
                                            buildNumber: '296',
                                            id: 'ac1fa4d1-8664-4679-ae19-9842afaff37e',
                                            integrationId: 'apple-beta',
                                            isProcessed: true,
                                            processingState: 'VALID',
                                            type: 'ios-binary',
                                            uploadedAt: '2022-03-15T11:50:03-07:00',
                                        },
                                        associatedCIBuild: {
                                            branch: 'main',
                                            buildFinishedAt: '2022-03-15T18:46:11Z',
                                            buildHash: '0qVMy0jBTbwzL5k67ZzU092K-wA=',
                                            buildIdentifier: '296',
                                            buildStartedAt: '2022-03-15T18:35:29Z',
                                            buildStatus: 'success',
                                            buildUrl: 'https://app.bitrise.io/build/123',
                                            commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                            commitAuthor: null,
                                            commitMessage:
                                                'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main` for release 3.97.0.',
                                            integrationId: 'bitrise',
                                            providerBuildStatusString: 'Success',
                                            workflowRunId: '482375f7-5c67-46a0-b3f3-0e24907c2e3c',
                                        },
                                        stabilityInfos: {},
                                    },
                                    isConsideredExternal: true,
                                    isLatestBuild: false,
                                    isSelectedBuild: false,
                                    metrics: { numInstalls: 0 },
                                    selectedReason: '',
                                },
                                {
                                    betaGroups: [
                                        '6c1c4439-16cb-49e8-b7c4-97d7c1f46a51',
                                        '7874adb9-9af0-427f-aabd-f7d36bed9049',
                                    ],
                                    betaStatus: 'testing',
                                    betaTesters: [],
                                    betaTestingNotes: [
                                        {
                                            displayLocale: 'American English',
                                            id: 'ec4ab077-1e79-4be5-bcfc-7f67fd846b92',
                                            locale: 'en-US',
                                            notes: '',
                                        },
                                    ],
                                    buildId: 'bb1a73c7-7645-499d-852a-7a6a96c7de30',
                                    appStoreBuild: {
                                        appStoreBuild: {
                                            buildNumber: '290',
                                            id: 'bb1a73c7-7645-499d-852a-7a6a96c7de30',
                                            integrationId: 'apple-beta',
                                            isProcessed: true,
                                            processingState: 'VALID',
                                            type: 'ios-binary',
                                            uploadedAt: '2022-03-11T12:08:29-08:00',
                                        },
                                        associatedCIBuild: {
                                            branch: 'main',
                                            buildFinishedAt: '2022-03-11T20:05:46Z',
                                            buildHash: 'cYAkkfz3H8wQ_0fBrtp05sJ6uMA=',
                                            buildIdentifier: '290',
                                            buildStartedAt: '2022-03-11T19:55:17Z',
                                            buildStatus: 'success',
                                            buildUrl: 'https://app.bitrise.io/build/123',
                                            commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                            commitAuthor: null,
                                            commitMessage:
                                                'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main` for release 3.97.0.',
                                            integrationId: 'bitrise',
                                            providerBuildStatusString: 'Success',
                                            workflowRunId: '66dee2d1-be63-43d5-a8b8-24bb8f19efbc',
                                        },
                                        stabilityInfos: {},
                                    },
                                    betaBuildDetails: {
                                        autoNotifyEnabled: true,
                                        externalBuildState: 'IN_BETA_TESTING',
                                        internalBuildState: 'IN_BETA_TESTING',
                                    },
                                    isConsideredExternal: true,
                                    isLatestBuild: false,
                                    isSelectedBuild: false,
                                    metrics: { numInstalls: 0 },
                                    selectedReason: '',
                                },
                            ],
                            betaGroups: [
                                {
                                    id: '6c1c4439-16cb-49e8-b7c4-97d7c1f46a51',
                                    isExternalGroup: false,
                                    name: 'App Store Connect Users',
                                    testers: ['info@demo.com'],
                                },
                                {
                                    id: 'ddbcb2dd-a9b3-4763-ba95-9db9f6325c80',
                                    isExternalGroup: true,
                                    name: 'ITC.apps.tf.labels.CustomGroup1',
                                    testers: [
                                        'aearhart@demo.com',
                                        'sully@demo.com',
                                        'buzz@demo.com',
                                        'tester1@gmail.com',
                                        'tester2@gmail.com',
                                        'tester3@gmail.com',
                                    ],
                                },
                                {
                                    id: '06c1a5c1-72ee-4ed1-83fd-151229eb9543',
                                    isExternalGroup: true,
                                    name: 'Another external testing group',
                                    testers: null,
                                },
                                {
                                    id: '7874adb9-9af0-427f-aabd-f7d36bed9049',
                                    isExternalGroup: true,
                                    name: 'The Best People',
                                    testers: ['aearhart@demo.com', 'sully@demo.com', 'buzz@demo.com'],
                                },
                            ],
                            buildInfo: {
                                associatedAppStoreBuild: {
                                    betaBuildDetails: {
                                        autoNotifyEnabled: true,
                                        externalBuildState: 'IN_BETA_TESTING',
                                        internalBuildState: 'IN_BETA_TESTING',
                                    },
                                    buildNumber: '297',
                                    id: 'fdeaddb9-87af-446b-baff-91eb85d1446c',
                                    integrationId: 'testflightx',
                                    isProcessed: true,
                                    processingState: 'VALID',
                                    type: 'ios-binary',
                                    uploadedAt: '2022-03-15T12:31:21-07:00',
                                },
                                ciBuild: {
                                    artifacts: [
                                        {
                                            downloadUrl: 'https://demo.runway.team',
                                            fileName: 'app.dSYM.zip',
                                            size: 16,
                                        },
                                        {
                                            downloadUrl: 'https://demo.runway.team',
                                            fileName: 'app.ipa',
                                            size: 139,
                                        },
                                    ],
                                    branch: 'main',
                                    buildFinishedAt: '2022-03-15T19:28:41Z',
                                    buildHash: 'A5LZuDfhn3e6WNweXBzV8AR1w6c=',
                                    buildIdentifier: '297',
                                    buildStartedAt: '2022-03-15T19:17:48Z',
                                    buildStatus: 'success',
                                    buildUrl: 'https://app.bitrise.io/build/123',
                                    commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                                    commitAuthor: 'runway-github[bot]',
                                    commitMessage:
                                        'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main` for release 3.97.0.',
                                    integrationId: 'bitrise',
                                    providerBuildStatusString: 'Success',
                                    workflowRunId: '0a65a37e-ba7d-45d7-bd3f-3ec0ba117bd8',
                                },
                                isSelectedBuild: false,
                                selectedReason: 'autoRunwaySelection',
                            },
                            soakDidFinishEarly: true,
                            soakFinishedAt: '2022-03-04T18:05:50Z',
                            soakStartedAt: '2022-03-16T01:16:18.832845812Z',
                            testers: [
                                {
                                    email: 'tester1@gmail.com',
                                    firstName: 'Tester',
                                    id: '1',
                                    lastName: 'One',
                                },
                                {
                                    email: 'tester2@gmail.com',
                                    firstName: 'Tester',
                                    id: '2',
                                    lastName: 'Two',
                                },
                                {
                                    email: 'tester3@gmail.com',
                                    firstName: 'Tester',
                                    id: '3',
                                    lastName: 'Three',
                                },
                                {
                                    email: 'info@demo.com',
                                    firstName: 'Buzz',
                                    id: '4',
                                    lastName: 'Aldrin',
                                },
                                {
                                    email: 'tester5@gmail.com',
                                    firstName: 'Tester',
                                    id: '5',
                                    lastName: 'Five',
                                },
                                {
                                    email: 'tester6@gmail.com',
                                    firstName: 'Tester',
                                    id: '6',
                                    lastName: 'Six',
                                },
                                {
                                    email: 'tester7@gmail.com',
                                    firstName: 'Tester',
                                    id: '7',
                                    lastName: 'Seven',
                                },
                            ],
                        },
                        groupIndex: 1,
                        orderIndex: 2,
                        checklistItems: [],
                        automationRuns: iosStepAutomations['betaTesting'],
                    },
                    {
                        id: '1.0.0#screenshots',
                        type: 'screenshots',
                        description: 'Screenshots',
                        status: 'pending',
                        statusReasonString: 'Approval required.',
                        blockingConfig: 'Blocking',
                        data: {
                            isApproved: false,
                            requiresApproval: true,
                            screenshotLocalizations: [
                                {
                                    displayLocale: 'American English',
                                    locale: 'en-US',
                                    screenshotSets: [
                                        {
                                            screenshotDisplayType: 'APP_IPHONE_55',
                                            screenshots: [
                                                {
                                                    height: 2208,
                                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-iOS-1.png',
                                                    width: 1242,
                                                },
                                                {
                                                    height: 2208,
                                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-iOS-2.png',
                                                    width: 1242,
                                                },
                                            ],
                                        },
                                        {
                                            screenshotDisplayType: 'APP_IPHONE_65',
                                            screenshots: [
                                                {
                                                    height: 2688,
                                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-iOS-1.png',
                                                    width: 1242,
                                                },
                                                {
                                                    height: 2688,
                                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-iOS-2.png',
                                                    width: 1242,
                                                },
                                            ],
                                        },
                                    ],
                                },
                                {
                                    displayLocale: 'European Spanish',
                                    locale: 'es-ES',
                                    screenshotSets: [
                                        {
                                            screenshotDisplayType: 'APP_IPHONE_65',
                                            screenshots: [
                                                {
                                                    height: 2688,
                                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-iOS-1.png',
                                                    width: 1242,
                                                },
                                                {
                                                    height: 2688,
                                                    url: 'https://runway-img.s3.us-east-2.amazonaws.com/demo-app-iOS-2.png',
                                                    width: 1242,
                                                },
                                            ],
                                        },
                                    ],
                                },
                            ],
                            storeUrl: 'https://appstoreconnect.apple.com',
                        },
                        groupIndex: 2,
                        orderIndex: 0,
                        checklistItems: [
                            {
                                id: 'c03218kcu89v1j3i28cg',
                                title: 'Upload screenshots to ASC per the guidelines',
                                description:
                                    'Here are the [guidelines](https://help.apple.com/app-store-connect/#/dev910472ff2).',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'screenshots',
                                approverGroups: ['design'],
                                releaseTypes: 'all',
                                notifyEnabled: false,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: iosStepAutomations['screenshots'],
                    },
                    {
                        id: '1.0.0#metadata',
                        type: 'metadata',
                        description: 'Metadata',
                        status: 'pending',
                        statusReasonString: 'Waiting for manual approval',
                        blockingConfig: 'NonBlocking',
                        data: {
                            isApproved: false,
                            localizations: [
                                {
                                    description:
                                        'This is a fun demo app to show you how cool releasing can be using Runway!',
                                    displayLocale: 'American English',
                                    id: '3.97.0:en-US',
                                    keywords: 'mobile,releases',
                                    locale: 'en-US',
                                    promotionalText: null,
                                    status: 'ready',
                                    whatsNew:
                                        'Minor bugfixes, some cool new features, and some foundational changes to keep things running smoothly!!',
                                },
                                {
                                    description:
                                        'Un demo divertido para enseñarles que facil es desarrollar applicaciones usando Runway!',
                                    displayLocale: 'European Spanish',
                                    id: '3.97.0:es-ES',
                                    keywords: 'mobile,releases',
                                    locale: 'es-ES',
                                    promotionalText: null,
                                    status: 'ready',
                                    whatsNew: 'Correcciónes de errores y mejoras menores.',
                                },
                            ],
                            requiresApproval: true,
                            storeUrl: 'https://appstoreconnect.apple.com',
                        },
                        groupIndex: 2,
                        orderIndex: 1,
                        checklistItems: [],
                        automationRuns: iosStepAutomations['metadata'],
                    },
                    {
                        id: '1.0.0#approvals',
                        type: 'approvals',
                        description: 'Approvals',
                        status: 'pending',
                        statusReasonString: 'There are 13 items that still require approval.',
                        blockingConfig: 'Blocking',
                        data: {
                            approvalItems: [
                                {
                                    approverGroups: ['qa'],
                                    comment: null,
                                    description: null,
                                    id: 'c51sfnlachdb4vli3720',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: {
                                        status: 'approved',
                                        createdAt: '2022-02-27T23:05:20.886428625Z',
                                        createdBy: 'ssullenberger@demo.com',
                                    },
                                    statuses: ['inProgress', 'blocked', 'rejected', 'approved'],
                                    title: 'Mobile - Checkout',
                                    type: 'multi',
                                    owners: ['ssullenberger@demo.com'],
                                },
                                {
                                    approverGroups: [],
                                    comment: {
                                        comment: 'Verified on latest, no issues',
                                        createdBy: 'aearhart@demo.com',
                                        createdAt: '2022-02-27T23:00:20.886428625Z',
                                    },
                                    description: null,
                                    id: 'c51p2qdachdas7jctn8g',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: {
                                        status: 'approved',
                                        createdAt: '2022-02-27T23:05:20.886428625Z',
                                        createdBy: 'aearhart@demo.com',
                                    },
                                    statuses: ['inProgress', 'blocked', 'rejected', 'approved'],
                                    title: 'Security/Ops',
                                    type: 'multi',
                                    owners: [],
                                },
                                {
                                    approverGroups: ['engineer'],
                                    comment: null,
                                    description: null,
                                    id: 'c51p10dachdas7jctn6g',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: null,
                                    statuses: ['inProgress', 'blocked', 'rejected', 'approved'],
                                    title: 'Mobile - Core',
                                    type: 'multi',
                                    owners: ['narmstrong@demo.com', 'ssullenberger@demo.com'],
                                },
                                {
                                    approverGroups: ['marketing'],
                                    comment: null,
                                    description: null,
                                    id: 'c55mpltachdaceuvmaug',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: null,
                                    statuses: ['inProgress', 'blocked', 'rejected', 'approved'],
                                    title: 'Sign-off from marketing',
                                    type: 'multi',
                                    owners: [],
                                },
                                {
                                    approverGroups: ['pilot'],
                                    comment: null,
                                    description: null,
                                    id: 'c51sif8m221m3fc0c4hg',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: null,
                                    statuses: ['inProgress', 'blocked', 'rejected', 'approved'],
                                    title: 'Get signoff from security/infra',
                                    type: 'multi',
                                    owners: [],
                                },
                                {
                                    approverGroups: ['pilot'],
                                    comment: null,
                                    description: null,
                                    id: 'c51sif8m221m3fc0c4i0',
                                    notifyEnabled: true,
                                    oneOffForVersion: null,
                                    uid: 'abc',
                                    createdAt: '2022-02-27T23:05:20.886428625Z',
                                    releaseStepType: null,
                                    releaseTypes: 'all',
                                    status: null,
                                    statuses: ['inProgress', 'blocked', 'rejected', 'approved'],
                                    title: 'Get signoff from compliance',
                                    type: 'multi',
                                    owners: [],
                                },
                            ],
                        },
                        groupIndex: 3,
                        orderIndex: 0,
                        checklistItems: [],
                        automationRuns: iosStepAutomations['approvals'],
                    },
                    {
                        id: '1.0.0#submission',
                        type: 'submission',
                        description: 'Submission',
                        status: 'pending',
                        statusReasonString: '3 checklist items still need to be completed for this step.',
                        blockingConfig: 'Blocking',
                        data: {
                            appStoreData: {
                                allInfoPresentForSubmission: true,
                                earliestReleaseDate: null,
                                exportComplianceInfoUsesEncryption: false,
                                integrationId: 'apple',
                                phasedRelease: {
                                    dayNumber: 0,
                                    isManuallyAcceleratedPhasedRollout: false,
                                    isRunwayAcceleratedRollout: false,
                                    isRunwayPaused: false,
                                    startDate: null,
                                    state: 'INACTIVE',
                                    userFraction: 0,
                                },
                                releaseRequested: false,
                                releaseType: 'MANUAL',
                                reviewDetail: {
                                    contactEmail: 'aearhart@demo.com',
                                    contactFirstName: 'Amelia',
                                    contactLastName: 'Earhart',
                                    contactPhone: '+(123)456-7891',
                                    demoAccountName: null,
                                    demoAccountPassword: null,
                                    demoAccountRequired: false,
                                    notes: '',
                                },
                                state: 'PREPARE_FOR_SUBMISSION',
                            },
                            storeUrl: 'https://appstoreconnect.apple.com',
                            submittedAt: null,
                            workflowData: null,
                        },
                        groupIndex: 4,
                        orderIndex: 0,
                        checklistItems: [
                            {
                                id: 'c5n2h7ln86bo9gnibnr0',
                                title: 'Thing to do before submit',
                                description: 'Do this!',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'submission',
                                approverGroups: ['approver'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'c8al5bdn86bs44bjrpd0',
                                title: 'Another thing to remember before submitting',
                                description: 'All this stuff',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'submission',
                                approverGroups: ['pilot', 'pm'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'c8e0r55n86boufv00ong',
                                title: 'Important Pre-submit step',
                                description: 'Remember to do this before submitting.',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'submission',
                                approverGroups: ['pilot', 'pm'],
                                releaseTypes: 'all',
                                notifyEnabled: false,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: iosStepAutomations['submission'],
                    },
                    {
                        id: '1.0.0#storeReview',
                        type: 'storeReview',
                        description: 'App store review',
                        status: 'inactive',
                        statusReasonString: 'Waiting for submission.',
                        blockingConfig: 'Blocking',
                        data: {
                            appStoreData: {
                                allInfoPresentForSubmission: true,
                                earliestReleaseDate: null,
                                exportComplianceInfoUsesEncryption: false,
                                integrationId: 'apple',
                                phasedRelease: {
                                    dayNumber: 0,
                                    isManuallyAcceleratedPhasedRollout: false,
                                    isRunwayAcceleratedRollout: false,
                                    isRunwayPaused: false,
                                    startDate: null,
                                    state: 'INACTIVE',
                                    userFraction: 0,
                                },
                                releaseRequested: false,
                                releaseType: 'MANUAL',
                                reviewDetail: {
                                    contactEmail: 'buzz@demo.com',
                                    contactFirstName: 'Buzz',
                                    contactLastName: 'Aldrin',
                                    contactPhone: '+1(123)456-7891',
                                    demoAccountName: null,
                                    demoAccountPassword: null,
                                    demoAccountRequired: false,
                                    notes: '',
                                },
                                state: 'PREPARE_FOR_SUBMISSION',
                            },
                            approvedAt: null,
                            devRejectedAt: null,
                            startedReviewAt: null,
                            storeRejectedAt: null,
                            storeUrl: 'https://appstoreconnect.apple.com',
                            submittedAt: null,
                        },
                        groupIndex: 4,
                        orderIndex: 1,
                        checklistItems: [
                            {
                                id: 'c7oomvdn86bpp9lejr1g',
                                title: 'Release via managed publishing if needed',
                                description: null,
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'storeReview',
                                approverGroups: ['pilot'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                            {
                                id: 'c59kpldachdcretdibcg',
                                title: 'Many different users can deal with this',
                                description: 'engineers, qa and pms should be able to approve this',
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'storeReview',
                                approverGroups: ['engineer', 'qa', 'pm'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: iosStepAutomations['storeReview'],
                    },
                    {
                        id: '1.0.0#takeoff',
                        type: 'takeoff',
                        description: 'Release',
                        status: 'inactive',
                        statusReasonString: 'Waiting for App Store review to be completed...',
                        blockingConfig: 'Blocking',
                        data: {
                            appStoreData: {
                                allInfoPresentForSubmission: true,
                                earliestReleaseDate: null,
                                exportComplianceInfoUsesEncryption: false,
                                integrationId: 'apple',
                                phasedRelease: {
                                    dayNumber: 0,
                                    isManuallyAcceleratedPhasedRollout: false,
                                    isRunwayAcceleratedRollout: false,
                                    isRunwayPaused: false,
                                    startDate: null,
                                    state: 'INACTIVE',
                                    userFraction: 0,
                                },
                                releaseRequested: false,
                                releaseType: 'MANUAL',
                                reviewDetail: {
                                    contactEmail: 'buzz@demo.com',
                                    contactFirstName: 'Buzz',
                                    contactLastName: 'Aldrin',
                                    contactPhone: '+16507395445',
                                    demoAccountName: null,
                                    demoAccountPassword: null,
                                    demoAccountRequired: false,
                                    notes: 'Use a clean, itemized restaurant receipt to test.\nThere is no password for sign-in: enter an email address, and a secure sign-in link will be emailed.',
                                },
                                state: 'WAITING_FOR_REVIEW',
                            },
                            appStoreUrl: 'https://apps.apple.com',
                            storeUrl: 'https://appstoreconnect.apple.com',
                            tagData: {
                                codeDiffUrl: 'https://github.com/demo/demo-ios/compare',
                                expectedReleaseTagName: 'v3.97.0',
                                releaseTag: null,
                                releaseTagState: 'notTaggable',
                                taggableCommits: [],
                            },
                        },
                        groupIndex: 4,
                        orderIndex: 2,
                        checklistItems: [
                            {
                                id: 'bvv18nkfoo7cohhp7ju0',
                                title: 'Send announcement email to team@ distribution list',
                                description: null,
                                type: 'binary',
                                statuses: ['approved'],
                                releaseStepType: 'takeoff',
                                approverGroups: ['pilot'],
                                releaseTypes: 'all',
                                notifyEnabled: true,
                                oneOffForVersion: null,
                                uid: 'abc',
                                createdAt: '2022-02-27T23:05:20.886428625Z',
                                status: null,
                                comment: null,
                                owners: [],
                                incompleteReminderEnabled: false,
                                autocompleteAtTargetCompletionEnabled: false,
                                includeDescriptionInNotificationsEnabled: false,
                                orgTemplateId: '',
                            },
                        ],
                        automationRuns: iosStepAutomations['takeoff'],
                    },
                ],
        appStoreBuilds: [],
    },
    upcomingReleases: [],
    pastReleases: [],
});
